import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminNavbar } from '../components/Navbar';
import { masterAPI, productionAPI, aiAPI } from '../services/api';
import SpreadsheetViewerModal from '../components/SpreadsheetViewerModal';
import { SpreadsheetData } from '../services/spreadsheetViewer';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import BuildIcon from '@mui/icons-material/Build';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TimerIcon from '@mui/icons-material/Timer';
import SettingsIcon from '@mui/icons-material/Settings';
import OpacityIcon from '@mui/icons-material/Opacity';
import PrintIcon from '@mui/icons-material/Print';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  exportToExcel,
  exportMultipleSheets,
  formatBreakdownData,
  formatPrintOrdersData,
  formatMachineData,
  formatNewsprintData,
  formatPlateConsumptionData,
  formatDurationData,
  formatTowersData,
  formatWastesData,
  formatStatistics,
} from '../services/excelExport';
import html2canvas from 'html2canvas';
import '../styles/admin-dashboard.css';

interface Publication {
  id: number;
  name: string;
  code?: string;
  type: 'VK' | 'OSP' | 'TOI';
  location?: string;
}

interface ChartData {
  po: any[];
  machine: any[];
  lprs: any[];
  newsprint: any[];
}

type LprsStatus = 'Delayed' | 'Early' | 'On time';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0'];
const LPRS_STATUS_CONFIG: Record<LprsStatus, {
  recordsKey: string;
  lprsSecondsKey: string;
  color: string;
  label: string;
}> = {
  Delayed: {
    recordsKey: 'delayed_records',
    lprsSecondsKey: 'delayed_lprs_seconds',
    color: '#f44336',
    label: 'Delayed',
  },
  Early: {
    recordsKey: 'early_records',
    lprsSecondsKey: 'early_lprs_seconds',
    color: '#03a9f4',
    label: 'Early',
  },
  'On time': {
    recordsKey: 'on_time_records',
    lprsSecondsKey: 'on_time_lprs_seconds',
    color: '#4caf50',
    label: 'On time',
  },
};

const SCHEDULE_STATUS_METRICS = [
  { key: 'print_start_delay', label: 'PRINT START DELAY', color: '#ffb600', tone: 'delay' },
  { key: 'print_start_on_time', label: 'PRINT START ON-TIME', color: '#00a651', tone: 'on-time' },
  { key: 'print_finish_delay', label: 'PRINT FINISH DELAY', color: '#087fbd', tone: 'delay' },
  { key: 'print_finish_on_time', label: 'PRINT FINISH ON-TIME', color: '#00a651', tone: 'on-time' },
] as const;

const LPRS_OCCURRENCE_METRICS = [
  { key: 'lprs_delay', label: 'LPRS DELAY', color: '#d24b00', tone: 'delay' },
  { key: 'lprs_on_time', label: 'LPRS ON-TIME', color: '#24550d', tone: 'on-time' },
] as const;

const writePngToClipboard = async (pngBlob: Blob) => {
  const ClipboardItemConstructor = (window as any).ClipboardItem;
  if (!navigator.clipboard?.write || !ClipboardItemConstructor) {
    throw new Error('Image clipboard is not supported in this browser');
  }

  await navigator.clipboard.write([
    new ClipboardItemConstructor({ 'image/png': pngBlob }),
  ]);
};

const copyElementAsImage = async (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  const width = Math.ceil(rect.width);
  const height = Math.ceil(rect.height);

  if (!width || !height) {
    throw new Error('Nothing to copy');
  }

  const canvas = await html2canvas(element, {
    backgroundColor: '#ffffff',
    scale: Math.min(2, window.devicePixelRatio || 1),
    useCORS: true,
    allowTaint: false,
    logging: false,
    width,
    height,
    windowWidth: Math.max(document.documentElement.scrollWidth, width),
    windowHeight: Math.max(document.documentElement.scrollHeight, height),
  });

  const pngBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Unable to create image'));
    }, 'image/png');
  });

  await writePngToClipboard(pngBlob);
};

const CopyableImageBlock = ({
  children,
  className = '',
  copyLabel = 'Copy image',
}: {
  children: React.ReactNode;
  className?: string;
  copyLabel?: string;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copying' | 'copied'>('idle');

  const handleCopy = async () => {
    if (!contentRef.current || copyStatus === 'copying') return;

    setCopyStatus('copying');
    try {
      await copyElementAsImage(contentRef.current);
      setCopyStatus('copied');
      window.setTimeout(() => setCopyStatus('idle'), 1800);
    } catch (error: any) {
      setCopyStatus('idle');
      alert(error?.message || 'Unable to copy image');
    }
  };

  return (
    <div className={`copyable-chart-block ${className}`}>
      <button
        type="button"
        className="copy-chart-button"
        onClick={handleCopy}
        disabled={copyStatus === 'copying'}
        data-copy-control="true"
        title="Copy this chart as an image"
      >
        <ContentCopyIcon sx={{ fontSize: 16 }} />
        {copyStatus === 'copied' ? 'Copied' : copyStatus === 'copying' ? 'Copying...' : copyLabel}
      </button>
      <div ref={contentRef}>
        {children}
      </div>
    </div>
  );
};

// State to track which bar is being hovered (shared across component)
let hoveredBarDataKey: string | null = null;

// Custom Bar Shape that tracks mouse events
const CustomBarShape = (props: any) => {
  const { x, y, width, height, fill, dataKey } = props;
  
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        rx="6"
        ry="6"
        onMouseEnter={() => {
          hoveredBarDataKey = dataKey;
        }}
        onMouseLeave={() => {
          hoveredBarDataKey = null;
        }}
        style={{ cursor: 'pointer' }}
      />
    </g>
  );
};

// Custom tooltip component - shows only the hovered bar
const CustomBarTooltip = (props: any) => {
  const { active, payload } = props;
  
  if (active && payload && payload.length > 0) {
    // Filter payload to show only the hovered bar
    let displayData = payload[0];
    
    if (hoveredBarDataKey && payload.length > 1) {
      // Find the bar that matches the hovered dataKey
      const hoveredData = payload.find((item: any) => item.dataKey === hoveredBarDataKey);
      if (hoveredData) {
        displayData = hoveredData;
      }
    }
    
    return (
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        border: `3px solid ${displayData.color}`,
        borderRadius: '8px',
        padding: '12px 16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
      }}>
        <p style={{ margin: '0', fontSize: '13px', color: '#666', fontWeight: '600', marginBottom: '6px' }}>
          {displayData.name}
        </p>
        <p style={{ margin: 0, color: displayData.color, fontWeight: 'bold', fontSize: '20px' }}>
          {typeof displayData.value === 'number' ? displayData.value.toLocaleString() : displayData.value}
        </p>
      </div>
    );
  }
  
  return null;
};

const WASTE_MONTH_COLORS = ['#9E9E9E', '#ED7D31', '#5B9BD5', '#FFC000', '#70AD47', '#A5A5A5'];
const WASTE_LOCATION_COLORS = ['#5B9BD5', '#ED7D31', '#70AD47', '#A5A5A5', '#FFC000', '#4472C4', '#A9D18E'];
const PLATE_MONTH_COLORS = ['#A6A6A6', '#ED7D31', '#5B9BD5', '#FFC000', '#70AD47', '#4472C4'];

const formatWastePercent = (value: any) => `${(parseFloat(value || 0)).toFixed(2)}%`;

type WasteMonth = [string, string];

const formatWasteMonthLabel = (year: number, monthIndex: number) => {
  const month = new Date(year, monthIndex, 1).toLocaleString('en-US', { month: 'short' });
  return `${month}-${String(year).slice(-2)}`;
};

const getSortableMonthValue = (monthValue: any, fallbackLabel?: any) => {
  const rawValue = String(monthValue || '').trim();
  const rawLabel = String(fallbackLabel || '').trim();
  const candidates = [rawValue, rawLabel].filter(Boolean);
  const monthLookup: Record<string, number> = {
    jan: 1,
    feb: 2,
    mar: 3,
    apr: 4,
    may: 5,
    jun: 6,
    jul: 7,
    aug: 8,
    sep: 9,
    oct: 10,
    nov: 11,
    dec: 12,
  };

  for (const candidate of candidates) {
    const yearMonth = candidate.match(/^(\d{4})[-/](\d{1,2})/);
    if (yearMonth) {
      return (parseInt(yearMonth[1], 10) * 100) + parseInt(yearMonth[2], 10);
    }

    const labelMonth = candidate.match(/^([A-Za-z]{3,})[-\s/](\d{2,4})$/);
    if (labelMonth) {
      const month = monthLookup[labelMonth[1].slice(0, 3).toLowerCase()];
      const yearPart = parseInt(labelMonth[2], 10);
      const year = labelMonth[2].length === 2 ? 2000 + yearPart : yearPart;
      if (month && year) {
        return (year * 100) + month;
      }
    }

    const parsedDate = new Date(candidate);
    if (!Number.isNaN(parsedDate.getTime())) {
      return (parsedDate.getFullYear() * 100) + (parsedDate.getMonth() + 1);
    }
  }

  return Number.MAX_SAFE_INTEGER;
};

const parseDateParts = (dateString?: string) => {
  if (!dateString) return null;
  const [year, month] = dateString.split('-').map((part) => parseInt(part, 10));
  if (!year || !month) return null;
  return { year, monthIndex: month - 1 };
};

const getSelectedWasteMonths = (startDate?: string, endDate?: string, data: any[] = []): WasteMonth[] => {
  const start = parseDateParts(startDate);
  const end = parseDateParts(endDate);

  if (!start || !end) {
    return Array.from(
      new Map((data || []).map((item: any) => [String(item.month), item.month_label || item.month])).entries()
    ).sort(([a, aLabel], [b, bLabel]) => getSortableMonthValue(a, aLabel) - getSortableMonthValue(b, bLabel)) as WasteMonth[];
  }

  const months: WasteMonth[] = [];
  const cursor = new Date(start.year, start.monthIndex, 1);
  const endMonth = new Date(end.year, end.monthIndex, 1);

  while (cursor <= endMonth) {
    const year = cursor.getFullYear();
    const monthIndex = cursor.getMonth();
    const monthKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    months.push([monthKey, formatWasteMonthLabel(year, monthIndex)]);
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return months;
};

const sortWasteMonths = (months: WasteMonth[]) =>
  [...months].sort(([a, aLabel], [b, bLabel]) => getSortableMonthValue(a, aLabel) - getSortableMonthValue(b, bLabel));

const formatWholeNumber = (value: any) => Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const buildPlateConsumptionChart = (items: any[], selectedMonths: WasteMonth[]) => {
  const months = sortWasteMonths(selectedMonths.length > 0
    ? selectedMonths
    : Array.from(new Map((items || []).map((item: any) => [String(item.month), item.month_label || item.month])).entries()) as WasteMonth[]);
  const locations = Array.from(new Set((items || []).map((item: any) => item.location || 'Unknown'))).sort();

  const rows = locations.map((location) => {
    const row: any = { location };
    months.forEach(([month, label]) => {
      const found = (items || []).find((item: any) => String(item.month) === String(month) && (item.location || 'Unknown') === location);
      row[String(month)] = found ? Number(found.total_plates || 0) : 0;
      row[`${month}_label`] = label;
    });
    return row;
  });

  return { months, rows };
};

const getPlateYAxisMax = (rows: any[], months: WasteMonth[]) => {
  const maxValue = rows.reduce((max, row) => {
    const rowMax = months.reduce((innerMax, [month]) => Math.max(innerMax, Number(row[String(month)] || 0)), 0);
    return Math.max(max, rowMax);
  }, 0);
  return Math.max(100, Math.ceil((maxValue * 1.12) / 500) * 500);
};

const PlateValueLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  const numericValue = Number(value || 0);
  if (!numericValue || width < 8 || height < 20) return null;

  return (
    <text
      x={x + width / 2}
      y={y + Math.min(height - 6, Math.max(18, height / 2))}
      fill="#333"
      textAnchor="middle"
      dominantBaseline="middle"
      transform={`rotate(-90 ${x + width / 2} ${y + Math.min(height - 6, Math.max(18, height / 2))})`}
      style={{ fontSize: '11px', fontWeight: 700 }}
    >
      {formatWholeNumber(numericValue)}
    </text>
  );
};

const NewsprintLocationTonsLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  const numericValue = Number(value || 0);
  if (!numericValue || width < 8 || height < 20) return null;

  const labelX = x + width / 2;
  const labelY = y + Math.min(height - 6, Math.max(18, height / 2));
  return (
    <text
      x={labelX}
      y={labelY}
      fill="#333"
      textAnchor="middle"
      dominantBaseline="middle"
      transform={`rotate(-90 ${labelX} ${labelY})`}
      style={{ fontSize: '11px', fontWeight: 700 }}
    >
      {numericValue.toFixed(2)}
    </text>
  );
};

const PlateConsumptionMonthlyChart = ({
  data,
  totalPlates,
  startDate,
  endDate,
  label = 'MMCL-All',
}: {
  data: any[];
  totalPlates: number;
  startDate?: string;
  endDate?: string;
  label?: string;
}) => {
  const selectedMonths = getSelectedWasteMonths(startDate, endDate, data || []);
  const { months, rows } = buildPlateConsumptionChart(data || [], selectedMonths);
  const yAxisMax = getPlateYAxisMax(rows, months);

  if (!data || data.length === 0 || rows.length === 0 || months.length === 0) {
    return null;
  }

  return (
    <CopyableImageBlock>
      <div style={{ marginBottom: '30px', backgroundColor: '#fff', border: '2px solid #222', padding: '14px 12px 0 12px', overflowX: 'auto' }}>
        <div style={{ position: 'relative', minWidth: '720px' }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#111', fontSize: '18px', fontWeight: 700, textAlign: 'center' }}>
            Plate Consumption by Location
          </h4>
          <div style={{
            position: 'absolute',
            top: '18px',
            right: '16px',
            display: 'grid',
            gridTemplateColumns: '84px 64px',
            border: '2px solid #777',
            backgroundColor: '#fff',
            color: '#111',
            fontSize: '12px',
            fontWeight: 700,
            textAlign: 'center'
          }}>
            <span style={{ borderRight: '2px solid #777', padding: '4px 6px' }}>{label}</span>
            <span style={{ padding: '4px 6px' }}>{formatWholeNumber(totalPlates)}</span>
          </div>
          <ResponsiveContainer width="100%" height={330}>
            <BarChart data={rows} margin={{ top: 24, right: 0, left: 10, bottom: 0 }}>
              <CartesianGrid stroke="#d9d9d9" vertical={false} />
              <XAxis
                dataKey="location"
                tick={{ fontSize: 11, fill: '#111', fontWeight: 700 }}
                interval={0}
                height={35}
              />
              <YAxis
                domain={[0, yAxisMax]}
                tick={{ fontSize: 11, fill: '#333' }}
                tickFormatter={(value) => formatWholeNumber(value)}
                allowDecimals={false}
              />
              <Tooltip
                itemSorter={(item: any) => {
                  const index = months.findIndex(([month]) => String(month) === String(item.dataKey));
                  return index === -1 ? months.length : index;
                }}
                formatter={(value: any, name: any) => [formatWholeNumber(value), name]}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  border: '1px solid #888',
                  borderRadius: '2px',
                  padding: '8px 10px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}
              />
              {months.map(([month, monthLabel], idx) => (
                <Bar
                  key={String(month)}
                  dataKey={String(month)}
                  name={String(monthLabel)}
                  fill={PLATE_MONTH_COLORS[idx % PLATE_MONTH_COLORS.length]}
                  isAnimationActive={false}
                >
                  <LabelList content={<PlateValueLabel />} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ margin: '0 -12px' }}>
          <table style={{ width: '100%', minWidth: '720px', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '11px', tableLayout: 'fixed' }}>
            <tbody>
              <tr>
                <td style={{ width: '90px', border: '1px solid #d9d9d9', padding: '5px 6px', fontWeight: 700 }}></td>
                {rows.map((row: any) => (
                  <td key={row.location} style={{ border: '1px solid #d9d9d9', padding: '5px 6px', textAlign: 'center', fontWeight: 700 }}>
                    {row.location}
                  </td>
                ))}
              </tr>
              {months.map(([month, monthLabel], idx) => (
                <tr key={String(month)}>
                  <td style={{ border: '1px solid #d9d9d9', padding: '5px 6px', fontWeight: 700, color: '#111' }}>
                    <span style={{ display: 'inline-block', width: '9px', height: '9px', backgroundColor: PLATE_MONTH_COLORS[idx % PLATE_MONTH_COLORS.length], marginRight: '5px' }}></span>
                    {String(monthLabel)}
                  </td>
                  {rows.map((row: any) => (
                    <td key={`${row.location}-${String(month)}`} style={{ border: '1px solid #d9d9d9', padding: '5px 6px', textAlign: 'center', fontWeight: 700 }}>
                      {formatWholeNumber(row[String(month)] || 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </CopyableImageBlock>
  );
};

const NewsprintConsumptionByLocationChart = ({
  data,
  totalKgs,
  startDate,
  endDate,
  label = 'MMCL-All',
}: {
  data: any[];
  totalKgs: number;
  startDate?: string;
  endDate?: string;
  label?: string;
}) => {
  const selectedMonths = getSelectedWasteMonths(startDate, endDate, data || []);
  const months = sortWasteMonths(selectedMonths.length > 0
    ? selectedMonths
    : Array.from(new Map((data || []).map((item: any) => [String(item.month), item.month_label || item.month])).entries()) as WasteMonth[]);
  const locations = Array.from(new Set((data || []).map((item: any) => item.location || 'Unknown'))).sort();
  const rows = locations.map((location) => {
    const row: any = { location };
    months.forEach(([month, monthLabel]) => {
      const found = (data || []).find((item: any) =>
        String(item.month) === String(month) && (item.location || 'Unknown') === location
      );
      row[String(month)] = found ? Number(found.total_kgs || 0) / 1000 : 0;
      row[`${month}_label`] = monthLabel;
    });
    return row;
  });
  const maxTons = rows.reduce((max: number, row: any) =>
    Math.max(max, ...months.map(([month]) => Number(row[String(month)] || 0))), 0);
  const yAxisStep = maxTons <= 10 ? 1 : maxTons <= 100 ? 10 : 50;
  const yAxisMax = Math.max(yAxisStep, Math.ceil((maxTons * 1.12) / yAxisStep) * yAxisStep);

  if (!data || data.length === 0 || rows.length === 0 || months.length === 0) {
    return null;
  }

  return (
    <CopyableImageBlock>
      <div style={{ marginBottom: '30px', backgroundColor: '#fff', border: '2px solid #222', padding: '14px 12px 0 12px', overflowX: 'auto' }}>
        <div style={{ position: 'relative', minWidth: '720px' }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#111', fontSize: '18px', fontWeight: 700, textAlign: 'center' }}>
            Newsprint Consumption by Location (Tons)
          </h4>
          <div style={{
            position: 'absolute',
            top: '18px',
            right: '16px',
            display: 'grid',
            gridTemplateColumns: '84px 78px',
            border: '2px solid #777',
            backgroundColor: '#fff',
            color: '#111',
            fontSize: '12px',
            fontWeight: 700,
            textAlign: 'center',
          }}>
            <span style={{ borderRight: '2px solid #777', padding: '4px 6px' }}>{label}</span>
            <span style={{ padding: '4px 6px' }}>{(Number(totalKgs || 0) / 1000).toFixed(2)} T</span>
          </div>
          <ResponsiveContainer width="100%" height={330}>
            <BarChart data={rows} margin={{ top: 24, right: 0, left: 10, bottom: 0 }}>
              <CartesianGrid stroke="#d9d9d9" vertical={false} />
              <XAxis dataKey="location" tick={{ fontSize: 11, fill: '#111', fontWeight: 700 }} interval={0} height={35} />
              <YAxis
                domain={[0, yAxisMax]}
                tick={{ fontSize: 11, fill: '#333' }}
                tickFormatter={(value) => Number(value || 0).toFixed(2)}
                allowDecimals
              />
              <Tooltip
                itemSorter={(item: any) => {
                  const index = months.findIndex(([month]) => String(month) === String(item.dataKey));
                  return index === -1 ? months.length : index;
                }}
                formatter={(value: any, name: any) => [`${Number(value || 0).toFixed(2)} tons`, name]}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  border: '1px solid #888',
                  borderRadius: '2px',
                  padding: '8px 10px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                }}
              />
              {months.map(([month, monthLabel], idx) => (
                <Bar
                  key={String(month)}
                  dataKey={String(month)}
                  name={String(monthLabel)}
                  fill={PLATE_MONTH_COLORS[idx % PLATE_MONTH_COLORS.length]}
                  isAnimationActive={false}
                >
                  <LabelList content={<NewsprintLocationTonsLabel />} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ margin: '0 -12px' }}>
          <table style={{ width: '100%', minWidth: '720px', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '11px', tableLayout: 'fixed' }}>
            <tbody>
              <tr>
                <td style={{ width: '90px', border: '1px solid #d9d9d9', padding: '5px 6px', fontWeight: 700 }}></td>
                {rows.map((row: any) => (
                  <td key={row.location} style={{ border: '1px solid #d9d9d9', padding: '5px 6px', textAlign: 'center', fontWeight: 700 }}>
                    {row.location}
                  </td>
                ))}
              </tr>
              {months.map(([month, monthLabel], idx) => (
                <tr key={String(month)}>
                  <td style={{ border: '1px solid #d9d9d9', padding: '5px 6px', fontWeight: 700, color: '#111' }}>
                    <span style={{ display: 'inline-block', width: '9px', height: '9px', backgroundColor: PLATE_MONTH_COLORS[idx % PLATE_MONTH_COLORS.length], marginRight: '5px' }}></span>
                    {String(monthLabel)}
                  </td>
                  {rows.map((row: any) => (
                    <td key={`${row.location}-${String(month)}`} style={{ border: '1px solid #d9d9d9', padding: '5px 6px', textAlign: 'center', fontWeight: 700 }}>
                      {Number(row[String(month)] || 0).toFixed(2)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </CopyableImageBlock>
  );
};

const buildMonthlyPlateConsumptionData = (monthlyRows: any[] = []) => {
  return (monthlyRows || [])
    .map((row: any) => ({
      month: String(row.month || ''),
      monthLabel: row.month_label || row.month || '',
      totalPlates: Number(row.total_plates || 0),
    }))
    .filter((row) => row.month && row.totalPlates > 0)
    .sort((a, b) => getSortableMonthValue(a.month, a.monthLabel) - getSortableMonthValue(b.month, b.monthLabel));
};

const PlateTotalBarLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  const numericValue = Number(value || 0);
  if (!numericValue || height < 36 || width < 14) return null;

  const labelX = x + width / 2;
  const labelY = y + height / 2;

  return (
    <text
      x={labelX}
      y={labelY}
      fill="#1f2933"
      textAnchor="middle"
      dominantBaseline="middle"
      transform={`rotate(-90 ${labelX} ${labelY})`}
      style={{ fontSize: '10px', fontWeight: 800, pointerEvents: 'none' }}
    >
      {formatWholeNumber(numericValue)}
    </text>
  );
};

const PlateConsumptionTotalChart = ({ monthlyRows }: { monthlyRows: any[] }) => {
  const rows = buildMonthlyPlateConsumptionData(monthlyRows || []);

  if (rows.length === 0) {
    return null;
  }

  const maxValue = Math.max(...rows.map((row) => Number(row.totalPlates || 0)));
  const minValue = Math.min(...rows.map((row) => Number(row.totalPlates || 0)));
  const yAxisMin = Math.max(0, Math.floor((minValue * 0.96) / 500) * 500);
  const yAxisMax = Math.max(500, Math.ceil((maxValue * 1.04) / 500) * 500);

  return (
    <CopyableImageBlock>
      <div style={{
        marginBottom: '30px',
        backgroundColor: '#fff',
        border: '1px solid #d8d8d8',
        padding: '12px 14px 8px',
      }}>
        <h4 style={{ textAlign: 'center', margin: '0 0 8px 0', fontSize: '18px', color: '#555', fontWeight: 800 }}>
          Plate Consumption
        </h4>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={rows} margin={{ top: 8, right: 15, left: 0, bottom: 5 }}>
            <CartesianGrid stroke="#e6e6e6" vertical={false} />
            <XAxis
              dataKey="monthLabel"
              tick={{ fontSize: 12, fill: '#555', fontWeight: 700 }}
              interval={0}
            />
            <YAxis
              domain={[yAxisMin, yAxisMax]}
              tick={{ fontSize: 11, fill: '#555', fontWeight: 700 }}
              tickFormatter={(value) => formatWholeNumber(value)}
              allowDecimals={false}
            />
            <Tooltip
              formatter={(value: any, name: any, item: any) => {
                if (item?.dataKey === 'totalPlates') {
                  return [formatWholeNumber(value), 'Plates'];
                }
                return [value, name];
              }}
              labelFormatter={(label) => `${label}`}
            />
            <Bar dataKey="totalPlates" fill="#5B9BD5" barSize={34} isAnimationActive={false}>
              <LabelList content={<PlateTotalBarLabel />} />
            </Bar>
            <Line
              type="monotone"
              dataKey="totalPlates"
              stroke="#333"
              strokeWidth={2}
              strokeDasharray="2 3"
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </CopyableImageBlock>
  );
};

const formatVarianceNumber = (value: any) => Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const formatLakhsValue = (value: any) => Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const getMonthLabel = (dateString?: string) => {
  const date = parseLocalDate(dateString);
  return `${date.toLocaleString('en-US', { month: 'short' })}-${String(date.getFullYear()).slice(-2)}`;
};

const buildMonthlyAlpChartData = (records: any[] = []) => {
  const monthMap = new Map<string, { month: string; monthLabel: string; rawAlp: number }>();

  records.forEach((record: any) => {
    const datePart = String(record.record_date || '').split('T')[0].split(' ')[0];
    if (!datePart) return;

    const date = parseLocalDate(datePart);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const current = monthMap.get(monthKey) || {
      month: monthKey,
      monthLabel: getMonthLabel(datePart),
      rawAlp: 0,
    };
    current.rawAlp += Number(record.alp || 0);
    monthMap.set(monthKey, current);
  });

  const rows = Array.from(monthMap.values())
    .sort((a, b) => getSortableMonthValue(a.month, a.monthLabel) - getSortableMonthValue(b.month, b.monthLabel))
    .map((row) => ({
      ...row,
      alpLakhs: Math.round(row.rawAlp / 100000),
    }));

  return rows;
};

const MonthlyAlpBarLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  const numericValue = Number(value || 0);
  if (!numericValue || height < 34 || width < 14) return null;

  const labelX = x + width / 2;
  const labelY = y + height / 2;

  return (
    <text
      x={labelX}
      y={labelY}
      fill="#1f2933"
      textAnchor="middle"
      dominantBaseline="middle"
      transform={`rotate(-90 ${labelX} ${labelY})`}
      style={{ fontSize: '10px', fontWeight: 800, pointerEvents: 'none' }}
    >
      {formatLakhsValue(numericValue)}
    </text>
  );
};

const MonthlyAlpChart = ({ records }: { records: any[] }) => {
  const rows = buildMonthlyAlpChartData(records || []);

  if (rows.length === 0) {
    return null;
  }

  const maxValue = Math.max(...rows.map((row) => Number(row.alpLakhs || 0)));
  const yAxisMax = Math.max(10, Math.ceil((maxValue * 1.08) / 10) * 10);

  return (
    <CopyableImageBlock>
      <div style={{
        marginBottom: '30px',
        backgroundColor: '#fff',
        border: '1px solid #d8d8d8',
        padding: '12px 14px 8px',
      }}>
        <h4 style={{ textAlign: 'center', margin: '0 0 8px 0', fontSize: '18px', color: '#555', fontWeight: 800 }}>
          Pages Printed
        </h4>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={rows} margin={{ top: 8, right: 15, left: 0, bottom: 5 }}>
            <CartesianGrid stroke="#e6e6e6" vertical={false} />
            <XAxis
              dataKey="monthLabel"
              tick={{ fontSize: 12, fill: '#555', fontWeight: 700 }}
              interval={0}
            />
            <YAxis
              domain={[0, yAxisMax]}
              tick={{ fontSize: 11, fill: '#555', fontWeight: 700 }}
              tickFormatter={(value) => formatLakhsValue(value)}
              allowDecimals={false}
            />
            <Tooltip
              formatter={(value: any, name: any, item: any) => {
                if (item?.dataKey === 'alpLakhs') {
                  return [formatVarianceNumber(item?.payload?.rawAlp || 0), 'ALP'];
                }
                return [formatLakhsValue(value), name];
              }}
              labelFormatter={(label) => `${label}`}
            />
            <Bar dataKey="alpLakhs" fill="#5B9BD5" barSize={34} isAnimationActive={false}>
              <LabelList content={<MonthlyAlpBarLabel />} />
            </Bar>
            <Line
              type="monotone"
              dataKey="alpLakhs"
              stroke="#333"
              strokeWidth={2}
              strokeDasharray="2 3"
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <div style={{ color: '#666', fontSize: '11px', textAlign: 'right', marginTop: '2px' }}>
          Values shown in lakhs
        </div>
      </div>
    </CopyableImageBlock>
  );
};

const INK_COLORS = {
  cyan: '#5B9BD5',
  magenta: '#ED7D31',
  yellow: '#FFD966',
  black: '#767171',
};

const buildInkRows = (monthlyRows: any[] = []) =>
  (monthlyRows || [])
    .map((row: any) => ({
      month: String(row.month || ''),
      monthLabel: row.month_label || row.month || '',
      totalAlp: Number(row.total_alp || 0),
      cyanKg: Number(row.ink_c || 0),
      magentaKg: Number(row.ink_m || 0),
      yellowKg: Number(row.ink_y || 0),
      blackKg: Number(row.ink_k || 0),
      totalInkKgs: Number(row.total_ink_kgs || 0),
      cyanMileage: Number(row.cyan_mileage || 0),
      magentaMileage: Number(row.magenta_mileage || 0),
      yellowMileage: Number(row.yellow_mileage || 0),
      blackMileage: Number(row.black_mileage || 0),
    }))
    .filter((row) => row.month)
    .sort((a, b) => getSortableMonthValue(a.month, a.monthLabel) - getSortableMonthValue(b.month, b.monthLabel));

const InkBarLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  const numericValue = Number(value || 0);
  if (!numericValue || height < 34 || width < 10) return null;
  const labelX = x + width / 2;
  const labelY = y + height / 2;

  return (
    <text
      x={labelX}
      y={labelY}
      fill="#222"
      textAnchor="middle"
      dominantBaseline="middle"
      transform={`rotate(-90 ${labelX} ${labelY})`}
      style={{ fontSize: '10px', fontWeight: 800, pointerEvents: 'none' }}
    >
      {Number(numericValue).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
    </text>
  );
};

const InkTonsBarLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  const numericValue = Number(value || 0);
  if (!numericValue || height < 34 || width < 10) return null;
  const labelX = x + width / 2;
  const labelY = y + height / 2;

  return (
    <text
      x={labelX}
      y={labelY}
      fill="#222"
      textAnchor="middle"
      dominantBaseline="middle"
      transform={`rotate(-90 ${labelX} ${labelY})`}
      style={{ fontSize: '10px', fontWeight: 800, pointerEvents: 'none' }}
    >
      {`${numericValue.toFixed(2)} tons`}
    </text>
  );
};

const InkConsumptionChart = ({ monthlyRows }: { monthlyRows: any[] }) => {
  const rows = buildInkRows(monthlyRows).map((row) => ({
    ...row,
    totalInkTons: Number(row.totalInkKgs || 0) / 1000,
  }));
  if (rows.length === 0) return null;

  const maxValue = Math.max(...rows.map((row) => Number(row.totalInkTons || 0)));
  const yAxisMax = Math.max(5, Math.ceil((maxValue * 1.15) / 5) * 5);

  return (
    <CopyableImageBlock>
      <div style={{ marginBottom: '30px', backgroundColor: '#fff', border: '1px solid #d8d8d8', padding: '12px 14px 8px' }}>
        <h4 style={{ textAlign: 'center', margin: '0 0 8px 0', fontSize: '18px', color: '#555', fontWeight: 800 }}>
          Total Ink Consumption
        </h4>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={rows} margin={{ top: 12, right: 18, left: 0, bottom: 5 }}>
            <CartesianGrid stroke="#e6e6e6" vertical={false} />
            <XAxis dataKey="monthLabel" tick={{ fontSize: 12, fill: '#555', fontWeight: 700 }} interval={0} />
            <YAxis
              domain={[0, yAxisMax]}
              tick={{ fontSize: 11, fill: '#555', fontWeight: 700 }}
              tickFormatter={(value) => `${Number(value || 0).toFixed(2)} tons`}
              allowDecimals
            />
            <Tooltip
              formatter={(value: any, name: any, item: any) => [
                `${Number(value || 0).toFixed(2)} tons (${Number(item?.payload?.totalInkKgs || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} kg)`,
                'Total Ink',
              ]}
              labelFormatter={(label) => `${label}`}
            />
            <Bar dataKey="totalInkTons" name="Total Ink" fill="#5B9BD5" barSize={34} isAnimationActive={false}>
              <LabelList content={<InkTonsBarLabel />} />
            </Bar>
            <Line type="monotone" dataKey="totalInkTons" stroke="#333" strokeDasharray="2 3" strokeWidth={2} dot={false} tooltipType="none" legendType="none" isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </CopyableImageBlock>
  );
};

const InkMileageChart = ({ monthlyRows }: { monthlyRows: any[] }) => {
  const rows = buildInkRows(monthlyRows);
  if (rows.length === 0) return null;

  const maxValue = Math.max(...rows.map((row) => Math.max(row.cyanMileage, row.magentaMileage, row.yellowMileage, row.blackMileage)));
  const yAxisMax = Math.max(100, Math.ceil((maxValue * 1.18) / 1000) * 1000);
  const legendFormatter = (value: any) => {
    const labels: Record<string, string> = {
      cyanMileage: 'Poly. (Cyan)',
      magentaMileage: 'Poly. (Magenta)',
      yellowMileage: 'Poly. (Yellow)',
      blackMileage: 'Poly. (Black)',
    };
    return labels[String(value)] || String(value);
  };

  return (
    <CopyableImageBlock>
      <div style={{ marginBottom: '30px', backgroundColor: '#fff', border: '1px solid #d8d8d8', padding: '22px 18px 14px' }}>
        <h4 style={{ textAlign: 'center', margin: '0 0 6px 0', fontSize: '18px', color: '#555', fontWeight: 900, letterSpacing: '2px', textTransform: 'uppercase' }}>
          All Location Ink Milage / Average Page
        </h4>
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart data={rows} margin={{ top: 18, right: 18, left: 4, bottom: 0 }}>
            <CartesianGrid stroke="#e2e2e2" vertical />
            <XAxis dataKey="monthLabel" tick={{ fontSize: 11, fill: '#666', fontWeight: 700 }} interval={0} />
            <YAxis domain={[0, yAxisMax]} tick={{ fontSize: 11, fill: '#555', fontWeight: 700 }} tickFormatter={(value) => formatVarianceNumber(value)} allowDecimals={false} />
            <Tooltip
              formatter={(value: any, name: any) => [
                `${formatVarianceNumber(value)} ALP/kg`,
                legendFormatter(name),
              ]}
              labelFormatter={(label) => `${label}`}
            />
            <Legend wrapperStyle={{ fontSize: '11px', fontWeight: 700 }} />
            <Bar dataKey="cyanMileage" name="Cyan" fill={INK_COLORS.cyan} barSize={28} isAnimationActive={false}><LabelList content={<InkBarLabel />} /></Bar>
            <Bar dataKey="magentaMileage" name="Magenta" fill={INK_COLORS.magenta} barSize={28} isAnimationActive={false}><LabelList content={<InkBarLabel />} /></Bar>
            <Bar dataKey="yellowMileage" name="Yellow" fill={INK_COLORS.yellow} barSize={28} isAnimationActive={false}><LabelList content={<InkBarLabel />} /></Bar>
            <Bar dataKey="blackMileage" name="Black" fill={INK_COLORS.black} barSize={28} isAnimationActive={false}><LabelList content={<InkBarLabel />} /></Bar>
            <Line type="monotone" dataKey="cyanMileage" name="Poly. (Cyan)" stroke={INK_COLORS.cyan} strokeDasharray="3 3" strokeWidth={2} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="magentaMileage" name="Poly. (Magenta)" stroke={INK_COLORS.magenta} strokeDasharray="3 3" strokeWidth={2} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="yellowMileage" name="Poly. (Yellow)" stroke="#C9A100" strokeDasharray="3 3" strokeWidth={2} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="blackMileage" name="Poly. (Black)" stroke="#111" strokeDasharray="3 3" strokeWidth={2} dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
        <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '11px', tableLayout: 'fixed', marginTop: '0' }}>
          <tbody>
            <tr>
              <td style={{ border: '1px solid #d9d9d9', padding: '5px 6px', fontWeight: 700 }}></td>
              {rows.map((row: any) => (
                <td key={`month-${row.month}`} style={{ border: '1px solid #d9d9d9', padding: '5px 6px', textAlign: 'center', fontWeight: 800, color: '#555' }}>
                  {row.monthLabel}
                </td>
              ))}
            </tr>
            {[
              ['Cyan', 'cyanMileage', INK_COLORS.cyan],
              ['Magenta', 'magentaMileage', INK_COLORS.magenta],
              ['Yellow', 'yellowMileage', INK_COLORS.yellow],
              ['Black', 'blackMileage', INK_COLORS.black],
            ].map(([label, key, color]) => (
              <tr key={String(key)}>
                <td style={{ border: '1px solid #d9d9d9', padding: '5px 6px', fontWeight: 700 }}>
                  <span style={{ display: 'inline-block', width: '9px', height: '9px', backgroundColor: String(color), marginRight: '5px' }}></span>
                  {String(label)}
                </td>
                {rows.map((row: any) => (
                  <td key={`${String(key)}-${row.month}`} style={{ border: '1px solid #d9d9d9', padding: '5px 6px', textAlign: 'center', fontWeight: 700 }}>
                    {formatVarianceNumber(row[String(key)] || 0)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CopyableImageBlock>
  );
};

const buildMonthlyNewsprintConsumptionData = (monthlyRows: any[] = []) => {
  return (monthlyRows || [])
    .map((row: any) => ({
      month: String(row.month || ''),
      monthLabel: row.month_label || row.month || '',
      totalKgs: Number(row.total_kgs || 0),
    }))
    .filter((row) => row.month && row.totalKgs > 0)
    .sort((a, b) => getSortableMonthValue(a.month, a.monthLabel) - getSortableMonthValue(b.month, b.monthLabel))
    .map((row) => ({
      ...row,
      tons: Number((row.totalKgs / 1000).toFixed(2)),
    }));
};

const NewsprintTonsBarLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  const numericValue = Number(value || 0);
  if (!numericValue || height < 36 || width < 14) return null;

  const labelX = x + width / 2;
  const labelY = y + height / 2;

  return (
    <text
      x={labelX}
      y={labelY}
      fill="#1f2933"
      textAnchor="middle"
      dominantBaseline="middle"
      transform={`rotate(-90 ${labelX} ${labelY})`}
      style={{ fontSize: '10px', fontWeight: 800, pointerEvents: 'none' }}
    >
      {numericValue.toFixed(2)}
    </text>
  );
};

const NewsprintConsumptionChart = ({ monthlyRows }: { monthlyRows: any[] }) => {
  const rows = buildMonthlyNewsprintConsumptionData(monthlyRows || []);

  if (rows.length === 0) {
    return null;
  }

  const maxValue = Math.max(...rows.map((row) => Number(row.tons || 0)));
  const minValue = Math.min(...rows.map((row) => Number(row.tons || 0)));
  const yAxisMin = Math.max(0, Math.floor((minValue * 0.96) / 10) * 10);
  const yAxisMax = Math.max(10, Math.ceil((maxValue * 1.04) / 10) * 10);

  return (
    <CopyableImageBlock>
      <div style={{
        marginBottom: '30px',
        backgroundColor: '#fff',
        border: '1px solid #d8d8d8',
        padding: '12px 14px 8px',
      }}>
        <h4 style={{ textAlign: 'center', margin: '0 0 8px 0', fontSize: '18px', color: '#555', fontWeight: 800 }}>
          News Print Consumption
        </h4>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={rows} margin={{ top: 8, right: 15, left: 0, bottom: 5 }}>
            <CartesianGrid stroke="#e6e6e6" vertical={false} />
            <XAxis
              dataKey="monthLabel"
              tick={{ fontSize: 12, fill: '#555', fontWeight: 700 }}
              interval={0}
            />
            <YAxis
              domain={[yAxisMin, yAxisMax]}
              tick={{ fontSize: 11, fill: '#555', fontWeight: 700 }}
              tickFormatter={(value) => Number(value || 0).toFixed(2)}
              allowDecimals
            />
            <Tooltip
              formatter={(value: any, name: any, item: any) => {
                if (item?.dataKey === 'tons') {
                  return [`${Number(value || 0).toFixed(2)} tons (${formatVarianceNumber(item?.payload?.totalKgs || 0)} kgs)`, 'Newsprint'];
                }
                return [value, name];
              }}
              labelFormatter={(label) => `${label}`}
            />
            <Bar dataKey="tons" fill="#5B9BD5" barSize={34} isAnimationActive={false}>
              <LabelList content={<NewsprintTonsBarLabel />} />
            </Bar>
            <Line
              type="monotone"
              dataKey="tons"
              stroke="#333"
              strokeWidth={2}
              strokeDasharray="2 3"
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </CopyableImageBlock>
  );
};

const formatSignedVarianceNumber = (value: any) => {
  const numericValue = Number(value || 0);
  if (numericValue > 0) return `+${formatVarianceNumber(numericValue)}`;
  return formatVarianceNumber(numericValue);
};

const formatVariancePercent = (value: any) => {
  const numericValue = Number(value || 0);
  const formatted = `${Math.abs(numericValue).toFixed(2)}%`;
  if (numericValue > 0) return `+${formatted}`;
  if (numericValue < 0) return `-${formatted}`;
  return '0.00%';
};

const parseLocalDate = (dateString?: string) => {
  if (!dateString) return new Date();
  const [datePart] = String(dateString).split('T');
  const [year, month, day] = datePart.split('-').map((part) => parseInt(part, 10));
  if (!year || !month || !day) return new Date(dateString);
  return new Date(year, month - 1, day);
};

const formatReportDate = (dateString?: string) => {
  const date = parseLocalDate(dateString);
  return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
};

const getFinancialYearQuarterLabels = (targetDate: string | undefined) => {
  const date = parseLocalDate(targetDate);
  const financialYearStart = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return [
    `Apr-Jun${String(financialYearStart).slice(-2)}`,
    `Jul-Sep${String(financialYearStart).slice(-2)}`,
    `Oct-Dec${String(financialYearStart).slice(-2)}`,
    `Jan-Mar${String(financialYearStart + 1).slice(-2)}`,
  ];
};

const getTrendDayLabels = (targetDate: string | undefined) => {
  const target = parseLocalDate(targetDate);
  return Array.from({ length: 5 }, (_, index) => {
    const date = new Date(target);
    date.setDate(target.getDate() - (5 - index));
    return String(date.getDate()).padStart(2, '0');
  });
};

const VarianceSparkline = ({ trend }: { trend: any[] }) => {
  const values = (trend || []).slice(-5).map((item: any) => Number(item.total_po || 0));
  if (values.length === 0) return <span style={{ color: '#777' }}>-</span>;

  const width = 120;
  const height = 34;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const spread = Math.max(1, max - min);
  const pointData = values.map((value, index) => {
    const x = values.length === 1 ? width / 2 : (index / (values.length - 1)) * width;
    const y = height - 4 - ((value - min) / spread) * (height - 8);
    return { x, y, value, date: trend[index]?.date };
  });
  const points = pointData.map((point) => `${point.x},${point.y}`).join(' ');

  return (
    <svg width="120" height="34" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block', margin: '0 auto' }}>
      <title>
        {pointData.map((point) => `${formatReportDate(point.date)}: ${formatVarianceNumber(point.value)}`).join('\n')}
      </title>
      {[0, 1, 2, 3].map((line) => (
        <line key={`h-${line}`} x1="0" x2={width} y1={line * 9 + 4} y2={line * 9 + 4} stroke="#d7dce5" strokeWidth="0.6" />
      ))}
      {[0, 1, 2, 3, 4, 5].map((line) => (
        <line key={`v-${line}`} y1="0" y2={height} x1={line * 24} x2={line * 24} stroke="#e4e7ef" strokeWidth="0.5" />
      ))}
      <polyline points={points} fill="none" stroke="#1b1f29" strokeWidth="1.8" />
      {pointData.map((point, index) => (
        <circle key={`${point.date || index}-${point.value}`} cx={point.x} cy={point.y} r="3" fill="#1b1f29">
          <title>{`${formatReportDate(point.date)}: ${formatVarianceNumber(point.value)}`}</title>
        </circle>
      ))}
    </svg>
  );
};

const PrintOrderVarianceReport = ({ report }: { report: any }) => {
  const rows = (report?.rows || []) as any[];
  const targetDate = report?.target_date;
  const trendLabels = getTrendDayLabels(targetDate);
  const quarterLabels = getFinancialYearQuarterLabels(targetDate);

  if (rows.length === 0) {
    return (
      <div style={{ border: '2px solid #111', backgroundColor: '#eef7e8', marginBottom: '30px' }}>
        <div style={{
          backgroundColor: '#1f2632',
          color: '#fff',
          padding: '8px 14px',
          fontWeight: 800,
          fontSize: '18px',
          textAlign: 'center',
        }}>
          Copies Printed Variance Report
        </div>
        <div style={{ padding: '24px', textAlign: 'center', color: '#444' }}>
          <p style={{ margin: '0 0 12px', fontWeight: 700 }}>
            No selected-publication data is available for this date range.
          </p>
          <p style={{ margin: '0 0 16px', fontSize: '14px' }}>
            Select one or more publications for each location in Publications Directory.
          </p>
          <a
            href="/admin/manage-publications"
            style={{
              display: 'inline-block',
              padding: '9px 16px',
              borderRadius: '6px',
              backgroundColor: '#1976d2',
              color: '#fff',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '14px',
            }}
          >
            Manage Variance Publications
          </a>
        </div>
      </div>
    );
  }

  const totalToday = rows.reduce((sum, row) => sum + Number(row.today_po || 0), 0);
  const totalYesterday = rows.reduce((sum, row) => sum + Number(row.yesterday_po || 0), 0);
  const totalLast30Avg = rows.reduce((sum, row) => sum + Number(row.last_30_avg || 0), 0);
  const totalDiffVsYesterday = totalToday - totalYesterday;
  const totalDiffVs30 = totalToday - totalLast30Avg;
  const totalPercentVsYesterday = totalYesterday > 0 ? (totalDiffVsYesterday / totalYesterday) * 100 : 0;
  const totalPercentVs30 = totalLast30Avg > 0 ? (totalDiffVs30 / totalLast30Avg) * 100 : 0;
  const topPerformer = [...rows].sort((a, b) => Number(b.diff || 0) - Number(a.diff || 0))[0];
  const bottomPerformer = [...rows].sort((a, b) => Number(a.diff || 0) - Number(b.diff || 0))[0];
  const locationsUp = rows.filter((row) => Number(row.diff || 0) > 0).length;
  const locationsDown = rows.filter((row) => Number(row.diff || 0) < 0).length;
  const totalRow = {
    avg_qtr_2: rows.reduce((sum, row) => sum + Number(row.avg_qtr_2 || 0), 0),
    avg_qtr_3: rows.reduce((sum, row) => sum + Number(row.avg_qtr_3 || 0), 0),
    avg_qtr_4: rows.reduce((sum, row) => sum + Number(row.avg_qtr_4 || 0), 0),
    avg_qtr_1: rows.reduce((sum, row) => sum + Number(row.avg_qtr_1 || 0), 0),
  };

  const valueColor = (value: any) => Number(value || 0) < 0 ? '#d71920' : '#111';
  const headerCellStyle: React.CSSProperties = {
    border: '1px solid #777',
    padding: '6px 4px',
    lineHeight: 1.25,
    verticalAlign: 'middle',
  };
  const numberCellStyle: React.CSSProperties = {
    border: '1px solid #888',
    padding: '8px 4px',
    textAlign: 'center',
    lineHeight: 1.25,
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    fontSize: '12px',
  };
  const totalNumberCellStyle: React.CSSProperties = {
    border: '1px solid #777',
    padding: '9px 4px',
    textAlign: 'center',
    lineHeight: 1.25,
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    fontSize: '12px',
  };

  return (
    <CopyableImageBlock>
      <div style={{ border: '2px solid #111', backgroundColor: '#eef7e8', overflowX: 'auto', marginBottom: '30px' }}>
      <div style={{ width: '100%', minWidth: '1180px' }}>
        <div style={{ backgroundColor: '#1f2632', color: '#fff', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', padding: '6px 14px', fontWeight: 800, fontSize: '18px', letterSpacing: 0 }}>
          <div style={{ textAlign: 'center' }}>Copies Printed Variance Report</div>
          <div style={{ fontSize: '16px' }}>{formatReportDate(targetDate)}</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr 1.35fr 0.95fr 0.8fr', gap: '10px', padding: '8px 10px 10px' }}>
          <div style={{ backgroundColor: '#dfeeda', border: '2px solid #222', borderRadius: '18px', boxShadow: '0 2px 5px rgba(0,0,0,0.35) inset, 0 1px 3px rgba(0,0,0,0.25)', padding: '8px', textAlign: 'center', fontWeight: 800 }}>
            <div>TOTAL COPIES PRINTED (Today)</div>
            <div style={{ fontSize: '18px', marginTop: '4px' }}>{formatVarianceNumber(totalToday)}</div>
            <div style={{ marginTop: '8px' }}>Yesterday Copies</div>
            <div style={{ fontSize: '16px', marginTop: '4px' }}>{formatVarianceNumber(totalYesterday)}</div>
            <div style={{ marginTop: '8px' }}>Difference</div>
            <div style={{ marginTop: '4px', color: valueColor(totalDiffVsYesterday) }}>{formatSignedVarianceNumber(totalDiffVsYesterday)}</div>
            <div style={{ marginTop: '4px', color: valueColor(totalPercentVsYesterday) }}>{formatVariancePercent(totalPercentVsYesterday)}</div>
          </div>

          <div style={{ backgroundColor: '#dfeeda', border: '2px solid #222', borderRadius: '18px', boxShadow: '0 2px 5px rgba(0,0,0,0.35) inset, 0 1px 3px rgba(0,0,0,0.25)', padding: '8px', textAlign: 'center', fontWeight: 800 }}>
            <div>Last 30 days Average</div>
            <div style={{ fontSize: '18px', marginTop: '4px' }}>{formatVarianceNumber(totalLast30Avg)}</div>
            <div style={{ marginTop: '8px' }}>Today Copies</div>
            <div style={{ fontSize: '16px', marginTop: '4px' }}>{formatVarianceNumber(totalToday)}</div>
            <div style={{ marginTop: '8px' }}>Difference</div>
            <div style={{ marginTop: '4px', color: valueColor(totalDiffVs30) }}>{formatSignedVarianceNumber(totalDiffVs30)}</div>
            <div style={{ marginTop: '4px', color: valueColor(totalPercentVs30) }}>{formatVariancePercent(totalPercentVs30)}</div>
          </div>

          <div style={{ backgroundColor: '#dfeeda', border: '2px solid #222', borderRadius: '18px', boxShadow: '0 2px 5px rgba(0,0,0,0.35) inset, 0 1px 3px rgba(0,0,0,0.25)', padding: '8px', display: 'grid', gridTemplateColumns: '68px 1fr', alignItems: 'center', fontWeight: 800 }}>
            <div style={{ fontSize: '54px', textAlign: 'center' }}>🏆</div>
            <div style={{ textAlign: 'center' }}>
              <div>Top Performer</div>
              <div style={{ fontSize: '18px', marginTop: '4px' }}>{formatSignedVarianceNumber(topPerformer?.diff || 0)}</div>
              <div style={{ marginTop: '5px' }}>{topPerformer?.location_name || '-'}</div>
              <div style={{ marginTop: '5px' }}>{formatVariancePercent(topPerformer?.percent || 0)}</div>
            </div>
          </div>

          <div style={{ backgroundColor: '#dfeeda', border: '2px solid #222', borderRadius: '18px', boxShadow: '0 2px 5px rgba(0,0,0,0.35) inset, 0 1px 3px rgba(0,0,0,0.25)', padding: '8px', textAlign: 'center', fontWeight: 800 }}>
            <div>Bottom Performer</div>
            <div style={{ color: '#d71920', fontSize: '18px', marginTop: '10px' }}>{formatSignedVarianceNumber(bottomPerformer?.diff || 0)}</div>
            <div style={{ marginTop: '10px' }}>{bottomPerformer?.location_name || '-'}</div>
            <div style={{ color: '#d71920', marginTop: '10px' }}>{formatVariancePercent(bottomPerformer?.percent || 0)}</div>
          </div>

          <div style={{ backgroundColor: '#dfeeda', border: '2px solid #222', borderRadius: '18px', boxShadow: '0 2px 5px rgba(0,0,0,0.35) inset, 0 1px 3px rgba(0,0,0,0.25)', display: 'grid', gridTemplateColumns: '1fr 1px 1fr', alignItems: 'center', textAlign: 'center', fontWeight: 800 }}>
            <div>
              <div>Locations</div>
              <div>Up</div>
              <div style={{ color: '#3f7f48', fontSize: '28px', lineHeight: 1 }}>▲</div>
              <div style={{ fontSize: '18px' }}>{locationsUp}</div>
            </div>
            <div style={{ height: '80%', backgroundColor: '#9ab48d' }} />
            <div>
              <div>Locations</div>
              <div>Down</div>
              <div style={{ color: '#d71920', fontSize: '28px', lineHeight: 1 }}>▼</div>
              <div style={{ color: '#d71920', fontSize: '18px' }}>{locationsDown}</div>
            </div>
          </div>
        </div>

        <table style={{ width: '100%', minWidth: '1180px', borderCollapse: 'collapse', backgroundColor: '#fff', fontSize: '12px', tableLayout: 'fixed', lineHeight: 1.25 }}>
          <colgroup>
            <col style={{ width: '14%' }} />
            <col style={{ width: '6.2%' }} />
            <col style={{ width: '6.2%' }} />
            <col style={{ width: '6.2%' }} />
            <col style={{ width: '6.2%' }} />
            <col style={{ width: '7.2%' }} />
            <col style={{ width: '6.8%' }} />
            <col style={{ width: '5.3%' }} />
            <col style={{ width: '3%' }} />
            <col style={{ width: '3%' }} />
            <col style={{ width: '3%' }} />
            <col style={{ width: '3%' }} />
            <col style={{ width: '3%' }} />
            <col style={{ width: '7.2%' }} />
            <col style={{ width: '6.8%' }} />
            <col style={{ width: '5.7%' }} />
          </colgroup>
          <thead>
            <tr style={{ backgroundColor: '#d7ddef' }}>
              <th rowSpan={2} style={headerCellStyle}>Location</th>
              <th style={headerCellStyle}>Average<br />Qtr-1</th>
              <th style={headerCellStyle}>Average<br />Qtr-2</th>
              <th style={headerCellStyle}>Average<br />Qtr-3</th>
              <th style={headerCellStyle}>Average<br />Qtr-4</th>
              <th rowSpan={2} style={headerCellStyle}>Last 30<br />days avg</th>
              <th colSpan={2} style={headerCellStyle}>Vs 30 days average</th>
              <th colSpan={5} style={headerCellStyle}>Last 5 days trend</th>
              <th colSpan={3} style={headerCellStyle}>{formatReportDate(targetDate)} (Copies Printed)</th>
            </tr>
            <tr style={{ backgroundColor: '#d7ddef' }}>
              {quarterLabels.map((label) => (
                <th key={label} style={headerCellStyle}>{label}</th>
              ))}
              <th style={headerCellStyle}>Diff</th>
              <th style={headerCellStyle}>%</th>
              {trendLabels.map((label) => (
                <th key={label} style={headerCellStyle}>{label}</th>
              ))}
              <th style={headerCellStyle}>Today Copies</th>
              <th style={headerCellStyle}>Diff</th>
              <th style={headerCellStyle}>%</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row: any, index: number) => (
              <tr key={`${row.location_name}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#fbfbfb', fontWeight: 800 }}>
                <td style={{ border: '1px solid #888', padding: '8px 8px', lineHeight: 1.25, verticalAlign: 'middle', fontSize: '12px' }}>{row.location_name || 'Unknown'}</td>
                <td style={{ ...numberCellStyle, backgroundColor: '#fffba8' }}>{formatVarianceNumber(row.avg_qtr_1)}</td>
                <td style={{ ...numberCellStyle, backgroundColor: '#fffba8' }}>{formatVarianceNumber(row.avg_qtr_2)}</td>
                <td style={{ ...numberCellStyle, backgroundColor: '#fffba8' }}>{formatVarianceNumber(row.avg_qtr_3)}</td>
                <td style={{ ...numberCellStyle, backgroundColor: '#fffba8' }}>{formatVarianceNumber(row.avg_qtr_4)}</td>
                <td style={numberCellStyle}>{formatVarianceNumber(row.last_30_avg)}</td>
                <td style={{ ...numberCellStyle, color: valueColor(row.diff) }}>{formatSignedVarianceNumber(row.diff)}</td>
                <td style={{ ...numberCellStyle, color: valueColor(row.percent) }}>{formatVariancePercent(row.percent)}</td>
                <td colSpan={5} style={{ border: '1px solid #888', padding: '0 4px', backgroundColor: '#f8f9fd' }}><VarianceSparkline trend={row.trend || []} /></td>
                <td style={numberCellStyle}>{formatVarianceNumber(row.today_po)}</td>
                <td style={{ ...numberCellStyle, color: valueColor(row.today_po - row.yesterday_po) }}>{formatSignedVarianceNumber(row.today_po - row.yesterday_po)}</td>
                <td style={{ ...numberCellStyle, color: valueColor(row.yesterday_po > 0 ? ((row.today_po - row.yesterday_po) / row.yesterday_po) * 100 : 0) }}>
                  {formatVariancePercent(row.yesterday_po > 0 ? ((row.today_po - row.yesterday_po) / row.yesterday_po) * 100 : 0)}
                </td>
              </tr>
            ))}
            <tr style={{ backgroundColor: '#d7ddef', fontWeight: 900 }}>
              <td style={{ border: '1px solid #777', padding: '9px 8px', lineHeight: 1.25, verticalAlign: 'middle', fontSize: '12px' }}>Total</td>
              <td style={totalNumberCellStyle}>{formatVarianceNumber(totalRow.avg_qtr_1)}</td>
              <td style={totalNumberCellStyle}>{formatVarianceNumber(totalRow.avg_qtr_2)}</td>
              <td style={totalNumberCellStyle}>{formatVarianceNumber(totalRow.avg_qtr_3)}</td>
              <td style={totalNumberCellStyle}>{formatVarianceNumber(totalRow.avg_qtr_4)}</td>
              <td style={totalNumberCellStyle}>{formatVarianceNumber(totalLast30Avg)}</td>
              <td style={{ ...totalNumberCellStyle, color: valueColor(totalDiffVs30) }}>{formatSignedVarianceNumber(totalDiffVs30)}</td>
              <td style={{ ...totalNumberCellStyle, color: valueColor(totalPercentVs30) }}>{formatVariancePercent(totalPercentVs30)}</td>
              <td colSpan={5} style={{ border: '1px solid #777', padding: '0 4px', backgroundColor: '#eef2fb' }}></td>
              <td style={totalNumberCellStyle}>{formatVarianceNumber(totalToday)}</td>
              <td style={{ ...totalNumberCellStyle, color: valueColor(totalDiffVsYesterday) }}>{formatSignedVarianceNumber(totalDiffVsYesterday)}</td>
              <td style={{ ...totalNumberCellStyle, color: valueColor(totalPercentVsYesterday) }}>{formatVariancePercent(totalPercentVsYesterday)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      </div>
    </CopyableImageBlock>
  );
};

const buildMachineWastePercentChart = (items: any[], machineName: string, selectedMonths: WasteMonth[]) => {
  const machineRows = (items || []).filter((item: any) => (item.machine_name || 'Unknown') === machineName);
  const months = sortWasteMonths(selectedMonths.length > 0
    ? selectedMonths
    : Array.from(new Map(machineRows.map((item: any) => [String(item.month), item.month_label || item.month])).entries()) as WasteMonth[]);
  const locations = Array.from(new Set(machineRows.map((item: any) => item.location || 'Unknown'))).sort();

  const rows = locations.map((location) => {
    const row: any = { location };
    months.forEach(([month, label]) => {
      const found = machineRows.find((item: any) => item.month === month && (item.location || 'Unknown') === location);
      row[String(month)] = found ? parseFloat(found.waste_percentage || 0) : 0;
      row[`${month}_label`] = label;
    });
    return row;
  });

  return { months, rows };
};

const WastePercentLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  const numericValue = parseFloat(value || 0);
  if (!numericValue || width < 10 || height < 18) return null;

  return (
    <text
      x={x + width / 2}
      y={Math.max(14, y + height / 2)}
      fill="#333"
      textAnchor="middle"
      dominantBaseline="middle"
      transform={`rotate(-90 ${x + width / 2} ${Math.max(14, y + height / 2)})`}
      style={{ fontSize: '12px', fontWeight: 700 }}
    >
      {formatWastePercent(numericValue)}
    </text>
  );
};

const getWasteYAxisMax = (rows: any[], months: WasteMonth[]) => {
  const maxValue = rows.reduce((max, row) => {
    const rowMax = months.reduce((innerMax, [month]) => Math.max(innerMax, parseFloat(row[String(month)] || 0)), 0);
    return Math.max(max, rowMax);
  }, 0);
  return Math.max(5, Math.ceil((maxValue + 2) / 2) * 2);
};

const WastePercentMachineChart = ({
  machineName,
  data,
  months,
}: {
  machineName: string;
  data: any[];
  months: WasteMonth[];
}) => {
  const { months: chartMonths, rows } = buildMachineWastePercentChart(data, machineName, months);
  const yAxisMax = getWasteYAxisMax(rows, chartMonths);

  if (rows.length === 0 || chartMonths.length === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: '34px', backgroundColor: '#ffffff', border: '1px solid #222', padding: '14px 18px 10px 18px' }}>
      <h5 style={{ margin: '0 0 12px 0', color: '#111', fontSize: '20px', lineHeight: 1.2, fontWeight: 500, textAlign: 'center' }}>
        Total Waste Trend {machineName} Machines
      </h5>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', marginBottom: '8px', fontSize: '12px', fontWeight: 600 }}>
        {chartMonths.map(([month, label], idx) => (
          <span key={`legend-${month}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: WASTE_MONTH_COLORS[idx % WASTE_MONTH_COLORS.length] }}></span>
            {label}
          </span>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={360}>
        <BarChart data={rows} margin={{ top: 15, right: 15, left: 0, bottom: 8 }}>
          <CartesianGrid stroke="#d9d9d9" vertical={false} />
          <XAxis
            dataKey="location"
            tick={{ fontSize: 12, fill: '#111', fontWeight: 700 }}
            interval={0}
            height={35}
          />
          <YAxis
            domain={[0, yAxisMax]}
            tick={{ fontSize: 11, fill: '#555', fontWeight: 600 }}
            tickFormatter={(value) => `${Number(value).toFixed(2)}%`}
          />
          <Tooltip
            itemSorter={(item: any) => {
              const index = chartMonths.findIndex(([month]) => String(month) === String(item.dataKey));
              return index === -1 ? chartMonths.length : index;
            }}
            formatter={(value: any, name: any) => [
              formatWastePercent(value),
              name,
            ]}
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              border: '1px solid #aaa',
              borderRadius: '2px',
              padding: '10px 12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
            }}
          />
          {chartMonths.map(([month, label], idx) => (
            <Bar key={String(month)} dataKey={String(month)} name={String(label)} fill={WASTE_MONTH_COLORS[idx % WASTE_MONTH_COLORS.length]} isAnimationActive={false}>
              <LabelList content={<WastePercentLabel />} />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      <div style={{ overflowX: 'auto', marginTop: '20px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '12px', tableLayout: 'fixed' }}>
          <tbody>
            <tr>
              <td style={{ width: '110px', border: '1px solid #d9d9d9', padding: '5px 6px', fontWeight: 700 }}></td>
              {rows.map((row: any) => (
                <td key={row.location} style={{ border: '1px solid #d9d9d9', padding: '5px 6px', textAlign: 'center', fontWeight: 700 }}>
                  {row.location}
                </td>
              ))}
            </tr>
            {chartMonths.map(([month, label], idx) => (
              <tr key={String(month)}>
                <td style={{ border: '1px solid #d9d9d9', padding: '5px 6px', fontWeight: 700, color: '#111' }}>
                  <span style={{ display: 'inline-block', width: '9px', height: '9px', backgroundColor: WASTE_MONTH_COLORS[idx % WASTE_MONTH_COLORS.length], marginRight: '5px' }}></span>
                  {String(label)}
                </td>
                {rows.map((row: any) => (
                  <td key={`${row.location}-${String(month)}`} style={{ border: '1px solid #d9d9d9', padding: '5px 6px', textAlign: 'center', fontWeight: 700 }}>
                    {formatWastePercent(row[String(month)] || 0)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const WastePercentComparisonSection = ({
  data,
  startDate,
  endDate,
}: {
  data: any[];
  startDate?: string;
  endDate?: string;
}) => {
  const machineNames = Array.from(new Set((data || []).map((item: any) => item.machine_name || 'Unknown'))).sort();
  const months = getSelectedWasteMonths(startDate, endDate, data || []);

  if (!data || data.length === 0 || machineNames.length === 0) {
    return (
      <div style={{ marginBottom: '30px', backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px' }}>
        <p style={{ color: '#999', fontStyle: 'italic', margin: 0 }}>No month-on-month waste percentage data available</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(520px, 1fr))', gap: '18px' }}>
      {machineNames.map((machineName) => (
        <WastePercentMachineChart key={machineName} machineName={machineName} data={data} months={months} />
      ))}
    </div>
  );
};

const buildWasteGeneratedData = (monthlyRows: any[] = [], wasteKey = 'total_print_waste_kgs') => {
  return (monthlyRows || [])
    .map((row: any) => ({
      month: String(row.month || ''),
      monthLabel: row.month_label || row.month || '',
      wastePercentage: Number(row.waste_percentage || 0),
      totalWasteKgs: Number(row[wasteKey] || row.total_waste_kgs || row.total_print_waste_kgs || 0),
      totalNewsprintKgs: Number(row.total_newsprint_kgs || 0),
    }))
    .filter((row) => row.month)
    .sort((a, b) => getSortableMonthValue(a.month, a.monthLabel) - getSortableMonthValue(b.month, b.monthLabel));
};

const PrintWasteGeneratedLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  const numericValue = Number(value || 0);
  if (!numericValue || height < 36 || width < 14) return null;

  const labelX = x + width / 2;
  const labelY = y + height / 2;

  return (
    <text
      x={labelX}
      y={labelY}
      fill="#1f2933"
      textAnchor="middle"
      dominantBaseline="middle"
      transform={`rotate(-90 ${labelX} ${labelY})`}
      style={{ fontSize: '10px', fontWeight: 800, pointerEvents: 'none' }}
    >
      {formatWastePercent(numericValue)}
    </text>
  );
};

const WasteGeneratedChart = ({
  monthlyRows,
  title,
  seriesLabel,
  noDataText,
  wasteKey = 'total_print_waste_kgs',
}: {
  monthlyRows: any[];
  title: string;
  seriesLabel: string;
  noDataText: string;
  wasteKey?: string;
}) => {
  const rows = buildWasteGeneratedData(monthlyRows || [], wasteKey);

  if (rows.length === 0) {
    return (
      <div style={{ marginBottom: '30px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #ddd' }}>
        <p style={{ color: '#999', fontStyle: 'italic', margin: 0 }}>{noDataText}</p>
      </div>
    );
  }

  const maxValue = Math.max(...rows.map((row) => Number(row.wastePercentage || 0)));
  const minValue = Math.min(...rows.map((row) => Number(row.wastePercentage || 0)));
  const yAxisMin = Math.max(0, Math.floor((minValue - 0.4) * 10) / 10);
  const yAxisMax = Math.max(1, Math.ceil((maxValue + 0.25) * 10) / 10);

  return (
    <CopyableImageBlock>
      <div style={{
        marginBottom: '30px',
        backgroundColor: '#fff',
        border: '1px solid #d8d8d8',
        padding: '12px 14px 8px',
      }}>
        <h4 style={{ textAlign: 'center', margin: '0 0 8px 0', fontSize: '18px', color: '#555', fontWeight: 800 }}>
          {title}
        </h4>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={rows} margin={{ top: 8, right: 15, left: 0, bottom: 5 }}>
            <CartesianGrid stroke="#e6e6e6" vertical={false} />
            <XAxis
              dataKey="monthLabel"
              tick={{ fontSize: 12, fill: '#555', fontWeight: 700 }}
              interval={0}
            />
            <YAxis
              domain={[yAxisMin, yAxisMax]}
              tick={{ fontSize: 11, fill: '#555', fontWeight: 700 }}
              tickFormatter={(value) => formatWastePercent(value)}
              allowDecimals
            />
            <Tooltip
              formatter={(value: any, name: any, item: any) => {
                if (item?.dataKey === 'wastePercentage') {
                  return [
                    `${formatWastePercent(value)} (${Number(item?.payload?.totalWasteKgs || 0).toFixed(2)} kg / ${Number(item?.payload?.totalNewsprintKgs || 0).toFixed(2)} kg)`,
                    seriesLabel,
                  ];
                }
                return [value, name];
              }}
              labelFormatter={(label) => `${label}`}
            />
            <Bar dataKey="wastePercentage" fill="#5B9BD5" barSize={34} isAnimationActive={false}>
              <LabelList content={<PrintWasteGeneratedLabel />} />
            </Bar>
            <Line
              type="monotone"
              dataKey="wastePercentage"
              stroke="#333"
              strokeWidth={2}
              strokeDasharray="2 3"
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </CopyableImageBlock>
  );
};

const buildNewsprintCategoryTrendChart = (
  items: any[],
  machineName: string,
  category: string,
  selectedMonths: WasteMonth[]
) => {
  const machineRows = (items || []).filter((item: any) =>
    (item.machine_name || 'Unknown') === machineName &&
    String(item.waste_category || '').toLowerCase() === String(category || '').toLowerCase()
  );
  const months = sortWasteMonths(selectedMonths.length > 0
    ? selectedMonths
    : Array.from(new Map(machineRows.map((item: any) => [String(item.month), item.month_label || item.month])).entries()) as WasteMonth[]);
  const locations = Array.from(new Set(machineRows.map((item: any) => item.location || 'Unknown'))).sort();

  const rows = months.map(([month, label]) => {
    const row: any = { month, month_label: label };
    locations.forEach((location) => {
      const found = machineRows.find((item: any) => item.month === month && (item.location || 'Unknown') === location);
      row[location] = found ? parseFloat(found.waste_percentage || 0) : 0;
    });
    return row;
  });

  return { months, locations, rows };
};

const NewsprintCategoryMachineChart = ({
  machineName,
  category,
  data,
  months,
}: {
  machineName: string;
  category: string;
  data: any[];
  months: WasteMonth[];
}) => {
  const { months: chartMonths, locations, rows } = buildNewsprintCategoryTrendChart(data, machineName, category, months);
  const yAxisMax = getWasteYAxisMax(rows, locations.map((location) => [location, location] as WasteMonth));

  if (rows.length === 0 || locations.length === 0 || chartMonths.length === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: '34px', backgroundColor: '#ffffff', border: '1px solid #d9d9d9', padding: '14px 18px 10px 18px' }}>
      <h5 style={{ margin: '0 0 12px 0', color: '#111', fontSize: '16px', lineHeight: 1.2, fontWeight: 700, textAlign: 'center' }}>
        {machineName} - {category}
      </h5>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={rows} margin={{ top: 18, right: 15, left: 0, bottom: 8 }}>
          <CartesianGrid stroke="#d9d9d9" vertical={false} />
          <XAxis
            dataKey="month_label"
            tick={{ fontSize: 12, fill: '#111', fontWeight: 700 }}
            interval={0}
            height={35}
          />
          <YAxis
            domain={[0, yAxisMax]}
            tick={{ fontSize: 11, fill: '#555', fontWeight: 600 }}
            tickFormatter={(value) => `${Number(value).toFixed(2)}%`}
          />
          <Tooltip
            formatter={(value: any, name: any) => [
              formatWastePercent(value),
              name,
            ]}
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              border: '1px solid #aaa',
              borderRadius: '2px',
              padding: '10px 12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
            }}
          />
          {locations.map((location, idx) => (
            <Bar key={location} dataKey={location} name={location} fill={WASTE_LOCATION_COLORS[idx % WASTE_LOCATION_COLORS.length]} isAnimationActive={false}>
              <LabelList content={<WastePercentLabel />} />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      <div style={{ overflowX: 'auto', marginTop: '14px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '12px', tableLayout: 'fixed' }}>
          <tbody>
            <tr>
              <td style={{ width: '120px', border: '1px solid #d9d9d9', padding: '5px 6px', fontWeight: 700 }}></td>
              {chartMonths.map(([month, label]) => (
                <td key={month} style={{ border: '1px solid #d9d9d9', padding: '5px 6px', textAlign: 'center', fontWeight: 700 }}>
                  {label}
                </td>
              ))}
            </tr>
            {locations.map((location, idx) => (
              <tr key={location}>
                <td style={{ border: '1px solid #d9d9d9', padding: '5px 6px', fontWeight: 700, color: '#111' }}>
                  <span style={{ display: 'inline-block', width: '9px', height: '9px', backgroundColor: WASTE_LOCATION_COLORS[idx % WASTE_LOCATION_COLORS.length], marginRight: '5px' }}></span>
                  {location}
                </td>
                {chartMonths.map(([month]) => {
                  const row = rows.find((item: any) => item.month === month);
                  return (
                    <td key={`${location}-${month}`} style={{ border: '1px solid #d9d9d9', padding: '5px 6px', textAlign: 'center', fontWeight: 700 }}>
                      {formatWastePercent(row?.[location] || 0)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const NewsprintCategoryTrendSection = ({
  data,
  category,
  startDate,
  endDate,
}: {
  data: any[];
  category: string;
  startDate?: string;
  endDate?: string;
}) => {
  const filtered = (data || []).filter((item: any) =>
    String(item.waste_category || '').toLowerCase() === String(category || '').toLowerCase()
  );
  const machineNames = Array.from(new Set(filtered.map((item: any) => item.machine_name || 'Unknown'))).sort();
  const months = getSelectedWasteMonths(startDate, endDate, data || []);

  if (!category || filtered.length === 0 || machineNames.length === 0) {
    return (
      <div style={{ marginBottom: '30px', backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px' }}>
        <p style={{ color: '#999', fontStyle: 'italic', margin: 0 }}>No month-on-month category trend data available</p>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '30px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(520px, 1fr))', gap: '18px' }}>
      {machineNames.map((machineName) => (
        <NewsprintCategoryMachineChart
          key={`${machineName}-${category}`}
          machineName={machineName}
          category={category}
          data={data}
          months={months}
        />
      ))}
    </div>
  );
};

// Helper function to format dates - show only date in IST timezone
const formatDateOnly = (dateString: string): string => {
  if (!dateString) return dateString;
  try {
    // Extract just the date part (YYYY-MM-DD) before any time component
    let datePart = dateString;
    if (dateString.includes('T')) {
      datePart = dateString.split('T')[0]; // Get YYYY-MM-DD
    } else if (dateString.includes(' ')) {
      datePart = dateString.split(' ')[0]; // Get YYYY-MM-DD
    }
    
    // Parse the date string
    const date = new Date(datePart);
    
    // Convert to IST timezone and format as DD-MMM-YYYY (e.g., 19-Feb-2026)
    const options: Intl.DateTimeFormatOptions = {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    };
    return new Intl.DateTimeFormat('en-IN', options).format(date);
  } catch (e) {
    // Fallback to simple string splitting if parsing fails
    const datePart = dateString.includes('T') ? dateString.split('T')[0] : dateString.split(' ')[0];
    return datePart || dateString;
  }
};

const formatSecondsAsTime = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const secs = safeSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const buildLprsConsolidatedRows = (records: any[] = []) => {
  const publicationMap = new Map<string, any>();

  (records || []).forEach((record: any) => {
    const publicationName = record.publication_name || 'Unknown';
    const current = publicationMap.get(publicationName) || {
      publication_name: publicationName,
      delayed_records: 0,
      delayed_lprs_seconds: 0,
      early_records: 0,
      early_lprs_seconds: 0,
      on_time_records: 0,
      on_time_lprs_seconds: 0,
      total_records: 0,
    };

    current.total_records += 1;
    const actualLprsSeconds = Number(record.actual_seconds || 0);

    if (record.status === 'Delayed') {
      current.delayed_records += 1;
      current.delayed_lprs_seconds += actualLprsSeconds;
    } else if (record.status === 'Early') {
      current.early_records += 1;
      current.early_lprs_seconds += actualLprsSeconds;
    } else {
      current.on_time_records += 1;
      current.on_time_lprs_seconds += actualLprsSeconds;
    }

    publicationMap.set(publicationName, current);
  });

  return Array.from(publicationMap.values())
    .sort((a, b) => b.total_records - a.total_records || String(a.publication_name).localeCompare(String(b.publication_name)));
};

export const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [vkPublications, setVkPublications] = useState<Publication[]>([]);
  const [ospPublications, setOspPublications] = useState<Publication[]>([]);
  const [toiPublications, setToiPublications] = useState<Publication[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [machines, setMachines] = useState<any[]>([]);
  
  // All machines (unfiltered)
  const [allMachines, setAllMachines] = useState<any[]>([]);
  
  // All publications (unfiltered)
  const [allVkPublications, setAllVkPublications] = useState<Publication[]>([]);
  const [allOspPublications, setAllOspPublications] = useState<Publication[]>([]);
  const [allToiPublications, setAllToiPublications] = useState<Publication[]>([]);

  // Selected publications
  const [selectedVkPub, setSelectedVkPub] = useState<string>('ALL');
  const [selectedOspPub, setSelectedOspPub] = useState<string>('ALL');
  const [selectedToiPub, setSelectedToiPub] = useState<string>('ALL');

  // Filters
  const [filters, setFilters] = useState({
    startDate: (() => {
      const now = new Date();
      const istTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      const year = istTime.getFullYear();
      const month = istTime.getMonth();
      const day = 1;
      const firstDay = new Date(year, month, day);
      const year_str = firstDay.getFullYear();
      const month_str = String(firstDay.getMonth() + 1).padStart(2, '0');
      const day_str = String(firstDay.getDate()).padStart(2, '0');
      return `${year_str}-${month_str}-${day_str}`;
    })(),
    endDate: (() => {
      const now = new Date();
      const istTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      const year_str = istTime.getFullYear();
      const month_str = String(istTime.getMonth() + 1).padStart(2, '0');
      const day_str = String(istTime.getDate()).padStart(2, '0');
      return `${year_str}-${month_str}-${day_str}`;
    })(),
    location: [] as string[],
    machine: '',
  });

  const [chartData, setChartData] = useState<ChartData>({
    po: [],
    machine: [],
    lprs: [],
    newsprint: [],
  }); // Used for legacy data storage

  const [newsprintAnalytics, setNewsprintAnalytics] = useState<any>(null); // Used in newsprint tab
  const [plateConsumptionAnalytics, setPlateConsumptionAnalytics] = useState<any>(null); // Used in plate tab
  const [machineAnalytics, setMachineAnalytics] = useState<any>(null); // Used in machine tab
  const [printOrdersAnalytics, setPrintOrdersAnalytics] = useState<any>(null); // Used in PO tab
  const [printDurationAnalytics, setPrintDurationAnalytics] = useState<any>(null); // Used in duration tab
  const [lprsDelayReport, setLprsDelayReport] = useState<any>(null);
  const [selectedLprsStatus, setSelectedLprsStatus] = useState<LprsStatus>('Delayed');
  const [wastesAnalytics, setWastesAnalytics] = useState<any>(null); // Used in wastes tab
  const [inkAnalytics, setInkAnalytics] = useState<any>(null); // Used in ink tab
  const [aiPredictions, setAIPredictions] = useState<any>(null); // Used in ai tab
  const [aiRecommendations, setAIRecommendations] = useState<any>(null); // Used in ai tab
  const [machineDowntimeData, setMachineDowntimeData] = useState<any[]>([]);
  const [machineDowntimeByMachine, setMachineDowntimeByMachine] = useState<any>(null); // Used in breakdown tab
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const analyticsRequestIdRef = useRef(0);

  // Sidebar tab state
  const [activeTab, setActiveTab] = useState<'breakdown' | 'po' | 'duration' | 'lprs' | 'machine' | 'plate' | 'newsprint' | 'wastes' | 'ink' | 'towers' | 'ai'>('breakdown');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Waste analytics filter state
  const [selectedNewsprintWaste, setSelectedNewsprintWaste] = useState<string>('Print waste');
  const [selectedPlatesWaste, setSelectedPlatesWaste] = useState<string>('ALL');
  
  // Waste section visibility state
  const [showNewsprintWaste, setShowNewsprintWaste] = useState<boolean>(true);
  const [showPlatesWaste, setShowPlatesWaste] = useState<boolean>(false);

  const getSelectedPublicationIds = useCallback((
    selectedValue: string,
    publications: Publication[],
  ): number[] => {
    if (selectedValue === 'ALL') {
      return publications.map((publication) => publication.id);
    }

    if (selectedValue) {
      const publicationId = parseInt(selectedValue, 10);
      return Number.isNaN(publicationId) ? [] : [publicationId];
    }

    return [];
  }, []);

  const getInkPublicationContext = useCallback(() => {
    if (selectedVkPub) {
      return {
        label: 'VK',
        publicationIds: getSelectedPublicationIds(selectedVkPub, vkPublications),
      };
    }

    if (selectedOspPub) {
      return {
        label: 'OSP',
        publicationIds: getSelectedPublicationIds(selectedOspPub, ospPublications),
      };
    }

    return {
      label: 'VK',
      publicationIds: getSelectedPublicationIds('ALL', vkPublications),
    };
  }, [getSelectedPublicationIds, selectedOspPub, selectedVkPub, ospPublications, vkPublications]);

  // Downtime detail modal state
  const [downtimeDetailModal, setDowntimeDetailModal] = useState<any>(null);
  const [downtimeDetails, setDowntimeDetails] = useState<any[]>([]);

  // Spreadsheet viewer state
  const [spreadsheetViewerOpen, setSpreadsheetViewerOpen] = useState(false);
  const [spreadsheetData, setSpreadsheetData] = useState<SpreadsheetData | null>(null);
  const [spreadsheetExportHandler, setSpreadsheetExportHandler] = useState<(() => void) | null>(null);
  const lprsConsolidatedRows = buildLprsConsolidatedRows(lprsDelayReport?.data || []);
  const selectedLprsConfig = LPRS_STATUS_CONFIG[selectedLprsStatus];
  const selectedLprsRows = lprsConsolidatedRows.filter((row: any) => Number(row[selectedLprsConfig.recordsKey] || 0) > 0);
  const lprsLocationRows = Array.from(
    (lprsDelayReport?.data || []).reduce((locationMap: Map<string, any>, record: any) => {
      const location = String(record.location || 'Unknown');
      const summary = locationMap.get(location) || { location, lprs_delay: 0, lprs_on_time: 0 };
      if (record.status === 'Delayed') summary.lprs_delay += 1;
      else summary.lprs_on_time += 1;
      locationMap.set(location, summary);
      return locationMap;
    }, new Map<string, any>()).values()
  ).sort((a: any, b: any) => a.location.localeCompare(b.location));
  const lprsOccurrenceMax = Math.max(
    1,
    ...lprsLocationRows.flatMap((row: any) =>
      LPRS_OCCURRENCE_METRICS.map((metric) => Number(row[metric.key] || 0))
    )
  );
  const scheduleStatusRows = printDurationAnalytics?.data || [];
  const scheduleStatusMax = Math.max(
    1,
    ...scheduleStatusRows.flatMap((row: any) =>
      SCHEDULE_STATUS_METRICS.map((metric) => Number(row[metric.key] || 0))
    )
  );
  const durationPeriodLabel = (() => {
    const startDate = new Date(`${filters.startDate}T00:00:00`);
    const endDate = new Date(`${filters.endDate}T00:00:00`);
    if (
      !Number.isNaN(startDate.getTime())
      && !Number.isNaN(endDate.getTime())
      && startDate.getFullYear() === endDate.getFullYear()
      && startDate.getMonth() === endDate.getMonth()
    ) {
      return new Intl.DateTimeFormat('en-IN', { month: 'long', year: 'numeric' })
        .format(startDate)
        .toUpperCase();
    }
    return `${filters.startDate} TO ${filters.endDate}`;
  })();

  // Export handlers for each tab
  const handleExportBreakdown = () => {
    if (!machineDowntimeData || machineDowntimeData.length === 0) {
      alert('No data to export');
      return;
    }
    const formattedData = formatBreakdownData(machineDowntimeData);
    const stats = formatStatistics({
      'Total Reasons': machineDowntimeData.length,
      'Total Occurrences': machineDowntimeData.reduce((sum: number, item: any) => sum + (item.total_occurrences || 0), 0),
      'Date Range': `${filters.startDate} to ${filters.endDate}`,
    }, 'Breakdown Analysis');
    exportMultipleSheets(
      [
        { data: stats, sheetName: 'Summary' },
        { data: formattedData, sheetName: 'Details' },
      ],
      'Breakdown-Analysis'
    );
  };

  const handleExportPrintOrders = () => {
    if (!printOrdersAnalytics?.by_publication || printOrdersAnalytics.by_publication.length === 0) {
      alert('No data to export');
      return;
    }
    const formattedPublications = formatPrintOrdersData(printOrdersAnalytics.by_publication);
    exportToExcel(formattedPublications, 'Publications', 'Print-Orders');
  };

  const handleExportMachine = () => {
    if (!machineAnalytics?.by_machine || machineAnalytics.by_machine.length === 0) {
      alert('No data to export');
      return;
    }
    const formattedData = formatMachineData(machineAnalytics.by_machine);
    const stats = formatStatistics(machineAnalytics.statistics || {}, 'Machine Analysis');
    exportMultipleSheets(
      [
        { data: stats, sheetName: 'Summary' },
        { data: formattedData, sheetName: 'Details' },
      ],
      'Machine-Analysis'
    );
  };

  const handleExportNewsprint = () => {
    if (!newsprintAnalytics?.daily_data || newsprintAnalytics.daily_data.length === 0) {
      alert('No data to export');
      return;
    }
    const formattedData = formatNewsprintData(newsprintAnalytics.daily_data);
    const stats = formatStatistics(newsprintAnalytics.statistics || {}, 'Newsprint Analysis');
    exportMultipleSheets(
      [
        { data: stats, sheetName: 'Summary' },
        { data: formattedData, sheetName: 'Details' },
      ],
      'Newsprint-Analysis'
    );
  };

  const handleExportPlate = () => {
    if (!plateConsumptionAnalytics?.daily_data || plateConsumptionAnalytics.daily_data.length === 0) {
      alert('No data to export');
      return;
    }
    const formattedData = formatPlateConsumptionData(plateConsumptionAnalytics.daily_data);
    const stats = formatStatistics(plateConsumptionAnalytics.statistics || {}, 'Plate Consumption');
    exportMultipleSheets(
      [
        { data: stats, sheetName: 'Summary' },
        { data: formattedData, sheetName: 'Details' },
      ],
      'Plate-Consumption-Analysis'
    );
  };

  const handleExportDuration = () => {
    if (!printDurationAnalytics?.daily_data || printDurationAnalytics.daily_data.length === 0) {
      alert('No data to export');
      return;
    }
    const formattedData = formatDurationData(printDurationAnalytics.daily_data);
    const stats = formatStatistics(printDurationAnalytics.statistics || {}, 'Duration Analysis');
    exportMultipleSheets(
      [
        { data: stats, sheetName: 'Summary' },
        { data: formattedData, sheetName: 'Details' },
      ],
      'Duration-Analysis'
    );
  };

  const handleExportTowers = () => {
    if (!chartData.po || chartData.po.length === 0) {
      alert('No data to export');
      return;
    }
    const formattedData = formatTowersData(chartData.po);
    const stats = formatStatistics({
      'Total Records': chartData.po.length,
      'Date Range': `${filters.startDate} to ${filters.endDate}`,
    }, 'Towers Analysis');
    exportMultipleSheets(
      [
        { data: stats, sheetName: 'Summary' },
        { data: formattedData, sheetName: 'Details' },
      ],
      'Towers-Analysis'
    );
  };

  const handleExportWastes = () => {
    if (!wastesAnalytics) {
      alert('No data to export');
      return;
    }
    const { newsprint, plates, monthlyNewsprint, monthlyPlates, monthlyWastePercent } = formatWastesData(
      wastesAnalytics.newsprint_by_location || [],
      wastesAnalytics.plates_wastes || [],
      wastesAnalytics.monthly_newsprint_by_location_machine || [],
      wastesAnalytics.monthly_plates_by_location_machine || [],
      wastesAnalytics.monthly_waste_percent_by_machine_location || []
    );
    const stats = formatStatistics({
      'Newsprint Records': wastesAnalytics.newsprint_by_location?.length || 0,
      'Plate Records': wastesAnalytics.plates_wastes?.length || 0,
      'Monthly Newsprint Rows': wastesAnalytics.monthly_newsprint_by_location_machine?.length || 0,
      'Monthly Plate Rows': wastesAnalytics.monthly_plates_by_location_machine?.length || 0,
      'Monthly Waste Percentage Rows': wastesAnalytics.monthly_waste_percent_by_machine_location?.length || 0,
    }, 'Wastes Analysis');
    exportMultipleSheets(
      [
        { data: stats, sheetName: 'Summary' },
        { data: newsprint, sheetName: 'Newsprint Waste' },
        { data: plates, sheetName: 'Plates Waste' },
        { data: monthlyWastePercent, sheetName: 'Monthly Waste Percent' },
        { data: monthlyNewsprint, sheetName: 'Monthly Newsprint' },
        { data: monthlyPlates, sheetName: 'Monthly Plates' },
      ],
      'Wastes-Analysis'
    );
  };

  // Spreadsheet Viewer Handlers for each tab
  const handleViewSpreadsheetBreakdown = () => {
    if (!machineDowntimeData || machineDowntimeData.length === 0) {
      alert('No data to view');
      return;
    }
    const data: SpreadsheetData = {
      title: 'Machine Downtime Breakdown',
      columns: [
        { key: 'reason', header: 'Downtime Reason', align: 'left' },
        { key: 'total_occurrences', header: 'Total Occurrences', align: 'center' },
        { key: 'avg_duration', header: 'Avg Duration (min)', align: 'center', format: (v) => (parseFloat(v) / 60).toFixed(2) },
        { key: 'machines_affected', header: 'Machines Affected', align: 'center' },
        { key: 'publications_affected', header: 'Publications Affected', align: 'center' },
      ],
      rows: machineDowntimeData,
    };
    handleOpenSpreadsheet(data, handleExportBreakdown);
  };

  const handleViewSpreadsheetPrintOrders = () => {
    if (!printOrdersAnalytics?.by_publication || printOrdersAnalytics.by_publication.length === 0) {
      alert('No data to view');
      return;
    }
    const data: SpreadsheetData = {
      title: 'No. of Copies Printed by Publication',
      columns: [
        { key: 'publication_name', header: 'Publication', align: 'left' },
        { key: 'total_pos', header: 'No. of Copies Printed', align: 'center' },
        { key: 'total_records', header: 'Records', align: 'center' },
        { key: 'total_pages', header: 'Total Pages', align: 'center' },
        { key: 'total_newsprint_waste', header: 'Newsprint Waste (Qty)', align: 'center' },
        { key: 'total_alp', header: 'ALP', align: 'center' },
      ],
      rows: printOrdersAnalytics.by_publication,
    };
    handleOpenSpreadsheet(data, handleExportPrintOrders);
  };

  const handleViewSpreadsheetMachine = () => {
    if (!machineAnalytics?.by_machine || machineAnalytics.by_machine.length === 0) {
      alert('No data to view');
      return;
    }
    const data: SpreadsheetData = {
      title: 'Machine Analytics',
      columns: [
        { key: 'machine_name', header: 'Machine', align: 'left' },
        { key: 'total_records', header: 'Total Records', align: 'center' },
        { key: 'total_pages', header: 'Total Pages', align: 'center' },
        { key: 'avg_pages_per_record', header: 'Avg Pages/Record', align: 'center', format: (v) => parseFloat(v).toFixed(2) },
        { key: 'publications_used', header: 'Publications', align: 'center' },
        { key: 'total_newsprint_kgs', header: 'Newsprint (Kg)', align: 'center', format: (v) => parseFloat(v).toFixed(2) },
      ],
      rows: machineAnalytics.by_machine,
    };
    handleOpenSpreadsheet(data, handleExportMachine);
  };

  const handleViewSpreadsheetNewsprint = () => {
    if (!newsprintAnalytics?.daily_data || newsprintAnalytics.daily_data.length === 0) {
      alert('No data to view');
      return;
    }
    const data: SpreadsheetData = {
      title: 'Newsprint Analytics',
      columns: [
        { key: 'date', header: 'Date', align: 'center', format: formatDate },
        { key: 'total_records', header: 'Total Records', align: 'center' },
        { key: 'total_kgs', header: 'Total KGs', align: 'center', format: (v) => parseFloat(v).toFixed(2) },
        { key: 'avg_kgs', header: 'Avg KGs/Record', align: 'center', format: (v) => parseFloat(v).toFixed(2) },
        { key: 'publications_count', header: 'Publications', align: 'center' },
      ],
      rows: newsprintAnalytics.daily_data,
    };
    handleOpenSpreadsheet(data, handleExportNewsprint);
  };

  const handleViewSpreadsheetPlate = () => {
    if (!plateConsumptionAnalytics?.daily_trend || plateConsumptionAnalytics.daily_trend.length === 0) {
      alert('No data to view');
      return;
    }
    const data: SpreadsheetData = {
      title: 'Plate Consumption Analysis',
      columns: [
        { key: 'date', header: 'Date', align: 'center', format: formatDate },
        { key: 'total_records', header: 'Total Records', align: 'center' },
        { key: 'total_plates', header: 'Total Plates', align: 'center' },
        { key: 'avg_plates_per_record', header: 'Avg Plates/Record', align: 'center', format: (v) => parseFloat(v).toFixed(2) },
      ],
      rows: plateConsumptionAnalytics.daily_trend,
    };
    handleOpenSpreadsheet(data, handleExportPlate);
  };

  const handleViewSpreadsheetDuration = () => {
    if (!printDurationAnalytics?.daily_data || printDurationAnalytics.daily_data.length === 0) {
      alert('No data to view');
      return;
    }
    const data: SpreadsheetData = {
      title: 'Print Duration Analysis',
      columns: [
        { key: 'date', header: 'Date', align: 'center', format: formatDate },
        { key: 'total_records', header: 'Total Records', align: 'center' },
        { key: 'avg_duration', header: 'Avg Duration (min)', align: 'center', format: (v) => (parseFloat(v) / 60).toFixed(2) },
        { key: 'total_time', header: 'Total Time (hrs)', align: 'center', format: (v) => (parseFloat(v) / 3600).toFixed(2) },
      ],
      rows: printDurationAnalytics.daily_data,
    };
    handleOpenSpreadsheet(data, handleExportDuration);
  };

  const handleViewSpreadsheetTowers = () => {
    if (!chartData.po || chartData.po.length === 0) {
      alert('No data to view');
      return;
    }
    const data: SpreadsheetData = {
      title: 'Towers Analysis',
      columns: [
        { key: 'date', header: 'Date', align: 'center', format: formatDate },
        { key: 'tower_1', header: 'Tower 1', align: 'center' },
        { key: 'tower_2', header: 'Tower 2', align: 'center' },
        { key: 'tower_3', header: 'Tower 3', align: 'center' },
        { key: 'tower_4', header: 'Tower 4', align: 'center' },
        { key: 'tower_5', header: 'Tower 5', align: 'center' },
      ],
      rows: chartData.po,
    };
    handleOpenSpreadsheet(data, handleExportTowers);
  };

  // Helper function to format dates
  const formatDate = (value: any): string => {
    if (!value) return '';
    try {
      const date = new Date(value);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return String(value);
    }
  };

  // Spreadsheet Viewer Handler
  const handleOpenSpreadsheet = (data: SpreadsheetData, exportHandler?: () => void) => {
    setSpreadsheetData(data);
    setSpreadsheetExportHandler(() => exportHandler || null);
    setSpreadsheetViewerOpen(true);
  };

  const getSelectedAnalyticsParams = useCallback(() => {
    const pubIds: number[] = [];

    if (selectedVkPub === 'ALL' && vkPublications.length > 0) {
      pubIds.push(...vkPublications.map((p) => p.id));
    } else if (selectedVkPub && selectedVkPub !== '' && selectedVkPub !== 'ALL') {
      pubIds.push(parseInt(selectedVkPub, 10));
    }

    if (selectedOspPub === 'ALL' && ospPublications.length > 0) {
      pubIds.push(...ospPublications.map((p) => p.id));
    } else if (selectedOspPub && selectedOspPub !== '' && selectedOspPub !== 'ALL') {
      pubIds.push(parseInt(selectedOspPub, 10));
    }

    if (selectedToiPub === 'ALL' && toiPublications.length > 0) {
      pubIds.push(...toiPublications.map((p) => p.id));
    } else if (selectedToiPub && selectedToiPub !== '' && selectedToiPub !== 'ALL') {
      pubIds.push(parseInt(selectedToiPub, 10));
    }

    return {
      publication_ids: pubIds.filter((id) => !Number.isNaN(id)).join(','),
      start_date: filters.startDate,
      end_date: filters.endDate,
      ...(filters.location.length > 0 && { location: filters.location.join(',') }),
      ...(filters.machine && { machine_id: filters.machine }),
    };
  }, [
    selectedVkPub,
    selectedOspPub,
    selectedToiPub,
    vkPublications,
    ospPublications,
    toiPublications,
    filters.startDate,
    filters.endDate,
    filters.location,
    filters.machine,
  ]);

  const loadLprsDelayReport = useCallback(async () => {
    const response = await productionAPI.getAnalyticsLPRSDelays(getSelectedAnalyticsParams());
    setLprsDelayReport(response.data || null);
  }, [getSelectedAnalyticsParams]);

  // Load master data on mount
  useEffect(() => {
    const loadMasterData = async (): Promise<void> => {
      try {
        console.log('Loading master data...');
        const [vkRes, ospRes, toiRes, locRes, machRes] = await Promise.all([
          masterAPI.getPublications('VK'),
          masterAPI.getPublications('OSP'),
          masterAPI.getPublications('TOI'),
          masterAPI.getLocations(),
          masterAPI.getMachines(),
        ]);

        const vkData = vkRes.data.data || vkRes.data;
        const ospData = ospRes.data.data || ospRes.data;
        const toiData = toiRes.data.data || toiRes.data;
        const locationData = locRes.data.data || locRes.data;
        const machineData = machRes.data.data || machRes.data;

        const vkArray = Array.isArray(vkData) ? vkData : [];
        const ospArray = Array.isArray(ospData) ? ospData : [];
        const toiArray = Array.isArray(toiData) ? toiData : [];
        const locationsArray = Array.isArray(locationData) ? locationData : [];
        const machinesArray = Array.isArray(machineData) ? machineData : [];

        setAllVkPublications(vkArray);
        setAllOspPublications(ospArray);
        setAllToiPublications(toiArray);
        setAllMachines(machinesArray);
        
        setVkPublications(vkArray);
        setOspPublications(ospArray);
        setToiPublications(toiArray);
        setLocations(locationsArray);
        setMachines(machinesArray);

        console.log('Master data loaded:', { vk: vkArray.length, osp: ospArray.length, toi: toiArray.length, locations: locationsArray.length, machines: machinesArray.length });
      } catch (error) {
        console.error('Error loading master data:', error);
        setError('Failed to load master data');
      } finally {
        setIsLoading(false);
      }
    };

    loadMasterData();
  }, []);

  // State to hold production records with tower data for towers tab
  const [towerRecordsData, setTowerRecordsData] = useState<any[]>([]);

  useEffect(() => {
    if (isLoading && vkPublications.length === 0 && ospPublications.length === 0 && toiPublications.length === 0) {
      return;
    }

    loadLprsDelayReport().catch((error) => {
      console.error('Error loading LPRS delay report:', error);
      setLprsDelayReport(null);
    });
  }, [
    isLoading,
    vkPublications.length,
    ospPublications.length,
    toiPublications.length,
    loadLprsDelayReport,
  ]);

  // Fetch analytics when filters or selected publications change
  useEffect(() => {
    const fetchAnalytics = async (): Promise<void> => {
      const requestId = ++analyticsRequestIdRef.current;
      const pubIds: number[] = [];

      if (selectedVkPub === 'ALL' && vkPublications.length > 0) {
        pubIds.push(...vkPublications.map((p) => p.id));
      } else if (selectedVkPub && selectedVkPub !== '' && selectedVkPub !== 'ALL') {
        pubIds.push(parseInt(selectedVkPub, 10));
      }

      if (selectedOspPub === 'ALL' && ospPublications.length > 0) {
        pubIds.push(...ospPublications.map((p) => p.id));
      } else if (selectedOspPub && selectedOspPub !== '' && selectedOspPub !== 'ALL') {
        pubIds.push(parseInt(selectedOspPub, 10));
      }

      if (selectedToiPub === 'ALL' && toiPublications.length > 0) {
        pubIds.push(...toiPublications.map((p) => p.id));
      } else if (selectedToiPub && selectedToiPub !== '' && selectedToiPub !== 'ALL') {
        pubIds.push(parseInt(selectedToiPub, 10));
      }

      // Only skip if publications still haven't loaded AND we're trying to load ALL
      if (pubIds.length === 0 && (selectedVkPub === 'ALL' || selectedOspPub === 'ALL' || selectedToiPub === 'ALL') && (vkPublications.length === 0 && ospPublications.length === 0 && toiPublications.length === 0)) {
        return; // Still loading publications, skip this call
      }

      setIsLoading(true);
      setError('');

      try {
        const params = {
          publication_ids: pubIds.join(','),
          start_date: filters.startDate,
          end_date: filters.endDate,
          ...(filters.location.length > 0 && { location: filters.location.join(',') }),
          ...(filters.machine && { machine_id: filters.machine }),
        };

        console.log('📊 Fetching analytics with params:', params);
        console.log('📊 pubIds:', pubIds, 'vkPublications:', vkPublications.length, 'ospPublications:', ospPublications.length, 'toiPublications:', toiPublications.length);

        const inkContext = getInkPublicationContext();
        const inkParams = {
          ...params,
          publication_ids: inkContext.publicationIds.length > 0
            ? inkContext.publicationIds.join(',')
            : '-1',
        };

        const [poRes, printOrdersRes, machineRes, machineDetailedRes, newsprintRes, newsprintKgsRes, plateRes, downtimeRes, downtimeByMachineRes, printDurationRes, wastesRes, inkRes, recordsRes] =
          await Promise.all([
            productionAPI.getAnalyticsPO(params),
            productionAPI.getAnalyticsPrintOrders(params),
            productionAPI.getAnalyticsMachine(params),
            productionAPI.getAnalyticsMachineDetailed(params),
            productionAPI.getAnalyticsNewsprint(params),
            productionAPI.getAnalyticsNewsprintKgs(params),
            productionAPI.getAnalyticsPlateConsumption(params),
            productionAPI.getAnalyticsDowntime(params),
            productionAPI.getAnalyticsDowntimeByMachine(params),
            productionAPI.getAnalyticsScheduleStatus(params),
            productionAPI.getAnalyticsWastes(params),
            productionAPI.getAnalyticsInk(inkParams),
            productionAPI.getRecords(params), // Fetch actual records with tower data for towers tab
          ]);

        // Ignore a response produced for an older filter selection.
        if (requestId !== analyticsRequestIdRef.current) return;

        // Fetch AI recommendations separately (don't block if ML service is down)
        try {
          const recRes = await aiAPI.getMaintenanceRecommendations(params);
          const predRes = await aiAPI.getMaintenancePredictions();
          if (requestId === analyticsRequestIdRef.current) {
            setAIRecommendations(recRes.data);
            setAIPredictions(predRes.data);
          }
        } catch (aiErr: any) {
          console.warn('⚠️ AI service unavailable:', aiErr.message);
          if (requestId === analyticsRequestIdRef.current) {
            setAIRecommendations(null);
            setAIPredictions(null);
          }
        }

        if (requestId !== analyticsRequestIdRef.current) return;

        // Store production records with tower data for towers tab
        const recordsData = recordsRes.data?.data || recordsRes.data || [];
        setTowerRecordsData(recordsData);

        setChartData({
          po: (poRes.data?.data || []).map((item: any) => ({
            ...item,
            date: item.date ? formatDateOnly(item.date) : item.date,
          })),
          machine: (machineRes.data?.data || []).map((item: any) => ({
            ...item,
            date: item.date ? formatDateOnly(item.date) : item.date,
          })),
          lprs: [],
          newsprint: (newsprintRes.data?.data || []).map((item: any) => ({
            ...item,
            date: item.date ? formatDateOnly(item.date) : item.date,
          })),
        });
        setNewsprintAnalytics({
          ...newsprintKgsRes.data,
          daily_trend: (newsprintKgsRes.data?.daily_trend || []).map((item: any) => ({
            ...item,
            date: item.date ? formatDateOnly(item.date) : item.date,
          })),
        });
        setPlateConsumptionAnalytics({
          ...plateRes.data,
          daily_trend: (plateRes.data?.daily_trend || []).map((item: any) => ({
            ...item,
            date: item.date ? formatDateOnly(item.date) : item.date,
          })),
          plate_per_page_trend: (plateRes.data?.plate_per_page_trend || []).map((item: any) => ({
            ...item,
            date: item.date ? formatDateOnly(item.date) : item.date,
          })),
        });
        setMachineAnalytics({
          ...machineDetailedRes.data,
          daily_trend: (machineDetailedRes.data?.daily_trend || []).map((item: any) => ({
            ...item,
            date: item.date ? formatDateOnly(item.date) : item.date,
          })),
        });
        setPrintOrdersAnalytics({
          ...printOrdersRes.data,
          daily_trend: (printOrdersRes.data?.daily_trend || []).map((item: any) => ({
            ...item,
            date: item.date ? formatDateOnly(item.date) : item.date,
          })),
        });
        setPrintDurationAnalytics(printDurationRes.data || { data: [] });
        setWastesAnalytics({
          ...wastesRes.data,
          daily_trend: (wastesRes.data?.daily_trend || []).map((item: any) => ({
            ...item,
            date: item.date ? formatDateOnly(item.date) : item.date,
          })),
        });
        setInkAnalytics(inkRes.data || null);
        
        const downtimeData = (downtimeRes.data?.data || []).map((item: any) => ({
          ...item,
          total_seconds: typeof item.total_seconds === 'string' ? parseInt(item.total_seconds, 10) : item.total_seconds,
        }));
        setMachineDowntimeData(downtimeData);
        setMachineDowntimeByMachine(downtimeByMachineRes.data || {});

        console.log('Analytics loaded');
      } catch (err: any) {
        console.error('Error fetching analytics:', err);
        if (requestId === analyticsRequestIdRef.current) {
          setError(err.response?.data?.error || 'Failed to load analytics');
        }
      } finally {
        if (requestId === analyticsRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchAnalytics();
  }, [
    selectedVkPub,
    selectedOspPub,
    selectedToiPub,
    filters.startDate,
    filters.endDate,
    filters.location,
    filters.machine,
    vkPublications,
    ospPublications,
    toiPublications,
    getInkPublicationContext,
  ]);

  const applyLocationSelection = (selectedLocations: string[]) => {
    const normalizedLocations = selectedLocations.filter(Boolean);

    setFilters((prev) => ({
      ...prev,
      location: normalizedLocations,
      machine: '',
    }));

    if (normalizedLocations.length === 0) {
      setVkPublications(allVkPublications);
      setOspPublications(allOspPublications);
      setToiPublications(allToiPublications);
      setMachines(allMachines);
    } else {
      const matchesSelectedLocation = (pub: Publication) =>
        pub.location && normalizedLocations.some((loc) => pub.location?.toLowerCase() === loc.toLowerCase());

      setVkPublications(allVkPublications.filter(matchesSelectedLocation));
      setOspPublications(allOspPublications.filter(matchesSelectedLocation));
      setToiPublications(allToiPublications.filter(matchesSelectedLocation));

      const fetchMachinesForLocation = async () => {
        try {
          if (normalizedLocations.length === 1) {
            const res = await masterAPI.getMachinesByLocation(normalizedLocations[0]);
            const machineData = res.data.data || res.data;
            const machinesArray = Array.isArray(machineData) ? machineData : [];
            setMachines(machinesArray);
          } else {
            setMachines(allMachines);
          }
        } catch (error) {
          console.error('Error fetching machines for location:', error);
          setMachines([]);
        }
      };
      fetchMachinesForLocation();
    }

    setSelectedVkPub('ALL');
    setSelectedOspPub('ALL');
    setSelectedToiPub('ALL');
  };

  const handleLocationToggle = (location: string) => {
    const selectedLocations = filters.location.includes(location)
      ? filters.location.filter((loc) => loc !== location)
      : [...filters.location, location];

    applyLocationSelection(selectedLocations);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'location') {
      setSelectedVkPub('ALL');
      setSelectedOspPub('ALL');
      setSelectedToiPub('ALL');
    }
  };

  const handleVkPubChange = (value: string) => {
    console.log('VK Selection changed to:', value);
    if (value === '') {
      setSelectedVkPub('ALL');
      setSelectedOspPub('');
      setSelectedToiPub('');
    } else if (value === 'ALL') {
      setSelectedVkPub('ALL');
      setSelectedOspPub('');
      setSelectedToiPub('');
    } else {
      setSelectedVkPub(value);
      setSelectedOspPub('');
      setSelectedToiPub('');
    }
  };

  const handleOspPubChange = (value: string) => {
    console.log('OSP Selection changed to:', value);
    if (value === '') {
      setSelectedOspPub('ALL');
      setSelectedVkPub('');
      setSelectedToiPub('');
    } else if (value === 'ALL') {
      setSelectedOspPub('ALL');
      setSelectedVkPub('');
      setSelectedToiPub('');
    } else {
      setSelectedOspPub(value);
      setSelectedVkPub('');
      setSelectedToiPub('');
    }
  };

  const handleToiPubChange = (value: string) => {
    console.log('TOI Selection changed to:', value);
    if (value === '') {
      setSelectedToiPub('ALL');
      setSelectedVkPub('');
      setSelectedOspPub('');
    } else if (value === 'ALL') {
      setSelectedToiPub('ALL');
      setSelectedVkPub('');
      setSelectedOspPub('');
    } else {
      setSelectedToiPub(value);
      setSelectedVkPub('');
      setSelectedOspPub('');
    }
  };

  const handleDowntimeRowClick = async (downtimeReason: any) => {
    console.log('Clicked downtime reason:', downtimeReason);
    setDowntimeDetailModal(downtimeReason);
    
    try {
      const publicationIds = [
        ...(selectedVkPub === 'ALL' ? vkPublications.map((p) => p.id) : selectedVkPub ? [parseInt(selectedVkPub, 10)] : []),
        ...(selectedOspPub === 'ALL' ? ospPublications.map((p) => p.id) : selectedOspPub ? [parseInt(selectedOspPub, 10)] : []),
        ...(selectedToiPub === 'ALL' ? toiPublications.map((p) => p.id) : selectedToiPub ? [parseInt(selectedToiPub, 10)] : []),
      ].filter((id) => !Number.isNaN(id)).join(',');

      const params = {
        publication_ids: publicationIds,
        start_date: filters.startDate,
        end_date: filters.endDate,
        ...(filters.location.length > 0 && { location: filters.location.join(',') }),
        ...(filters.machine && { machine_id: filters.machine }),
      };

      const response = await productionAPI.getDowntimeDetails(downtimeReason.id, params);
      const detailedRecords = response.data.data || [];

      console.log('Downtime details records:', detailedRecords);

      const allPublications = [...vkPublications, ...ospPublications, ...toiPublications];
      
      const publicationDurations: { [key: string]: { location: string; durations: string[] } } = {};
      
      detailedRecords.forEach((record: any) => {
        const pubName = record.publication_name;
        const pub = allPublications.find((p) => p.name === pubName);
        const location = pub?.location || 'Unknown';
        const key = `${pubName}|${location}`;
        
        if (!publicationDurations[key]) {
          publicationDurations[key] = { location, durations: [] };
        }
        
        let formattedDuration = '00:00:00';
        if (record.downtime_duration) {
          if (typeof record.downtime_duration === 'string' && record.downtime_duration.includes(':')) {
            formattedDuration = record.downtime_duration;
          } else {
            let duration = record.downtime_duration;
            if (typeof duration === 'string') {
              duration = parseInt(duration, 10);
            }
            
            if (duration && !isNaN(duration) && duration > 0) {
              const hours = Math.floor(duration / 3600);
              const minutes = Math.floor((duration % 3600) / 60);
              const seconds = Math.floor(duration % 60);
              formattedDuration = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
            }
          }
        }
        
        publicationDurations[key].durations.push(formattedDuration);
      });

      const publicationsWithDurations = Object.entries(publicationDurations)
        .map(([key, data]) => {
          const [pubName] = key.split('|');
          const durationsStr = data.durations.length > 0 ? data.durations.join(', ') : '00:00:00';
          return `${pubName} (${data.location}) - ${durationsStr}`;
        })
        .join('\n');

      setDowntimeDetails([
        {
          reason: downtimeReason.downtime_reason,
          category: downtimeReason.category,
          occurrences: downtimeReason.total_occurrences,
          totalDuration: downtimeReason.total_duration,
          avgDuration: Math.round(downtimeReason.total_seconds / downtimeReason.total_occurrences),
          publications: publicationsWithDurations || 'No publications affected',
        }
      ]);
    } catch (err) {
      console.error('❌ Error fetching downtime details:', err);
      const pubNamesWithLocation = [...vkPublications, ...ospPublications, ...toiPublications]
        .map((p) => `${p.name} (${p.location})`)
        .join(', ');

      setDowntimeDetails([
        {
          reason: downtimeReason.downtime_reason,
          category: downtimeReason.category,
          occurrences: downtimeReason.total_occurrences,
          totalDuration: downtimeReason.total_duration,
          avgDuration: Math.round(downtimeReason.total_seconds / downtimeReason.total_occurrences),
          publications: pubNamesWithLocation || 'All Publications',
        }
      ]);
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div>
        <AdminNavbar />
        <div className="admin-dashboard">
          <div className="error-message">Admin access required</div>
        </div>
      </div>
    );
  }

  if (isLoading && vkPublications.length === 0 && ospPublications.length === 0 && toiPublications.length === 0) {
    return (
      <div>
        <AdminNavbar />
        <div className="admin-dashboard">
          <div className="loading">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AdminNavbar />
      <div className="admin-dashboard">
        <div className="dashboard-header">
          <h1>Production Analytics Dashboard</h1>
          <p>Welcome, {user?.name}</p>
        </div>

        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}

        {/* FILTERS SECTION */}
        <div className="filters-container">
          <div className="filters-grid">
            <div className="filter-group">
              <label htmlFor="startDate">Start Date</label>
              <input
                id="startDate"
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="endDate">End Date</label>
              <input
                id="endDate"
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <label>Location</label>
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: '#fff',
                padding: '8px',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 600, marginBottom: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={filters.location.length === 0}
                    onChange={() => applyLocationSelection([])}
                  />
                  All Locations
                </label>
                {locations.map((loc) => (
                  <label key={loc} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '5px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={filters.location.includes(loc)}
                      onChange={() => handleLocationToggle(loc)}
                    />
                    {loc}
                  </label>
                ))}
              </div>
              {filters.location.length > 0 && (
                <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                  Selected: {filters.location.join(', ')}
                </small>
              )}
            </div>

            <div className="filter-group">
              <label htmlFor="machine">Machine</label>
              <select
                id="machine"
                name="machine"
                value={filters.machine}
                onChange={handleFilterChange}
              >
                <option value="">All Machines</option>
                {machines.map((mach: any) => (
                  <option key={mach.id} value={mach.id}>
                    {mach.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* PUBLICATION SELECTION SECTION */}
        <div className="publication-selection-container">
          <h3>Select Publications</h3>

          <div className="publications-dropdowns">
            <div className="dropdown-wrapper">
              <label htmlFor="vkPub">VK Publications</label>
              <select
                id="vkPub"
                value={selectedVkPub}
                onChange={(e) => handleVkPubChange(e.target.value)}
                className="publication-dropdown"
              >
                <option value="">-- Select VK Publication --</option>
                <option value="ALL">
                  ✓ All VK Publications ({vkPublications.length})
                </option>
                {vkPublications.map((pub) => (
                  <option key={`vk-${pub.id}`} value={pub.id}>
                    {pub.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="dropdown-wrapper">
              <label htmlFor="ospPub">OSP Publications</label>
              <select
                id="ospPub"
                value={selectedOspPub}
                onChange={(e) => handleOspPubChange(e.target.value)}
                className="publication-dropdown"
              >
                <option value="">-- Select OSP Publication --</option>
                <option value="ALL">
                  ✓ All OSP Publications ({ospPublications.length})
                </option>
                {ospPublications.map((pub) => (
                  <option key={`osp-${pub.id}`} value={pub.id}>
                    {pub.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="dropdown-wrapper">
              <label htmlFor="toiPub">TOI Publications</label>
              <select
                id="toiPub"
                value={selectedToiPub}
                onChange={(e) => handleToiPubChange(e.target.value)}
                className="publication-dropdown"
              >
                <option value="">-- Select TOI Publication --</option>
                <option value="ALL">
                  ✓ All TOI Publications ({toiPublications.length})
                </option>
                {toiPublications.map((pub) => (
                  <option key={`toi-${pub.id}`} value={pub.id}>
                    {pub.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* CHARTS SECTION WITH SIDEBAR TABS */}
        <div style={{ display: 'flex', gap: '20px', marginTop: '30px' }}>
          {/* SIDEBAR */}
          <div style={{
            width: sidebarOpen ? '200px' : '60px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            padding: '15px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'width 0.3s ease',
            height: 'fit-content',
            position: 'sticky',
            top: '20px'
          }}>
            {/* Sidebar Toggle Button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {sidebarOpen ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />}
            </button>

            {/* Tab Buttons */}
            {sidebarOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  onClick={() => setActiveTab('breakdown')}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'breakdown' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'breakdown' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <BuildIcon sx={{ fontSize: '18px' }} /> Breakdown
                </button>
                <button
                  onClick={() => setActiveTab('po')}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'po' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'po' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <AssignmentIcon sx={{ fontSize: '18px' }} /> No. of Copies Printed
                </button>
                <button
                  onClick={() => setActiveTab('duration')}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'duration' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'duration' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <TimerIcon sx={{ fontSize: '18px' }} /> Duration
                </button>
                <button
                  onClick={() => setActiveTab('lprs')}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'lprs' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'lprs' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <StackedBarChartIcon sx={{ fontSize: '18px' }} /> LPRS
                </button>
                <button
                  onClick={() => setActiveTab('machine')}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'machine' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'machine' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <SettingsIcon sx={{ fontSize: '18px' }} /> Machine
                </button>
                <button
                  onClick={() => setActiveTab('plate')}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'plate' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'plate' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <PrintIcon sx={{ fontSize: '18px' }} /> Plate
                </button>
                <button
                  onClick={() => setActiveTab('newsprint')}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'newsprint' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'newsprint' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <StackedBarChartIcon sx={{ fontSize: '18px' }} /> Newsprint
                </button>
                <button
                  onClick={() => setActiveTab('wastes')}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'wastes' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'wastes' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: '18px' }} /> Wastes
                </button>
                <button
                  onClick={() => setActiveTab('ink')}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'ink' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'ink' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <OpacityIcon sx={{ fontSize: '18px' }} /> Ink
                </button>
                <button
                  onClick={() => setActiveTab('towers')}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'towers' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'towers' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <BuildIcon sx={{ fontSize: '18px' }} /> Towers
                </button>
                <button
                  onClick={() => setActiveTab('ai')}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'ai' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'ai' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <SmartToyIcon sx={{ fontSize: '18px' }} /> AI Insights
                </button>
              </div>
            )}

            {/* Collapsed Icons */}
            {!sidebarOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div
                  onClick={() => { setSidebarOpen(true); setActiveTab('breakdown'); }}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'breakdown' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'breakdown' ? 'white' : '#333',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Breakdown"
                >
                  <BuildIcon />
                </div>
                <div
                  onClick={() => { setSidebarOpen(true); setActiveTab('po'); }}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'po' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'po' ? 'white' : '#333',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="No. of Copies Printed"
                >
                  <AssignmentIcon />
                </div>
                <div
                  onClick={() => { setSidebarOpen(true); setActiveTab('duration'); }}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'duration' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'duration' ? 'white' : '#333',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Duration"
                >
                  <TimerIcon />
                </div>
                <div
                  onClick={() => { setSidebarOpen(true); setActiveTab('lprs'); }}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'lprs' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'lprs' ? 'white' : '#333',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="LPRS"
                >
                  <StackedBarChartIcon />
                </div>
                <div
                  onClick={() => { setSidebarOpen(true); setActiveTab('machine'); }}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'machine' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'machine' ? 'white' : '#333',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Machine"
                >
                  <OpacityIcon />
                </div>
                <div
                  onClick={() => { setSidebarOpen(true); setActiveTab('plate'); }}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'plate' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'plate' ? 'white' : '#333',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Plate"
                >
                  <PrintIcon />
                </div>
                <div
                  onClick={() => { setSidebarOpen(true); setActiveTab('newsprint'); }}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'newsprint' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'newsprint' ? 'white' : '#333',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Newsprint"
                >
                  <StackedBarChartIcon />
                </div>
                <div
                  onClick={() => { setSidebarOpen(true); setActiveTab('wastes'); }}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'wastes' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'wastes' ? 'white' : '#333',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Wastes"
                >
                  <DeleteOutlineIcon />
                </div>
                <div
                  onClick={() => { setSidebarOpen(true); setActiveTab('ink'); }}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'ink' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'ink' ? 'white' : '#333',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Ink"
                >
                  <SettingsIcon />
                </div>
                <div
                  onClick={() => { setSidebarOpen(true); setActiveTab('ai'); }}
                  style={{
                    padding: '12px',
                    backgroundColor: activeTab === 'ai' ? '#2196F3' : '#e0e0e0',
                    color: activeTab === 'ai' ? 'white' : '#333',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="AI Insights"
                >
                  <SmartToyIcon />
                </div>
              </div>
            )}
          </div>

          {/* MAIN CONTENT AREA */}
          <div style={{ flex: 1 }}>
            {/* BREAKDOWN TAB */}
            {activeTab === 'breakdown' && (
              <div className="chart-wrapper">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h3>Machine Downtime Breakdown</h3>
                  <div style={{display: 'flex', gap: '10px'}}>
                    <button 
                      onClick={handleViewSpreadsheetBreakdown}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="View in spreadsheet"
                    >
                      📊 Spreadsheet
                    </button>
                    <button 
                      onClick={handleExportBreakdown}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="Download as Excel"
                    >
                      <FileDownloadIcon style={{fontSize: '18px'}} /> Export
                    </button>
                  </div>
                </div>
                {machineDowntimeData && machineDowntimeData.length > 0 ? (
                  <div style={{ width: '100%' }}>
                    {/* Statistics Cards */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '15px',
                      marginBottom: '25px'
                    }}>
                      <div style={{
                        backgroundColor: '#fff3e0',
                        border: '2px solid #ff9800',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOTAL DOWNTIME REASONS</p>
                        <p style={{ margin: 0, color: '#ff9800', fontSize: '28px', fontWeight: 'bold' }}>
                          {machineDowntimeData.length}
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#e3f2fd',
                        border: '2px solid #2196F3',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOTAL OCCURRENCES</p>
                        <p style={{ margin: 0, color: '#2196F3', fontSize: '28px', fontWeight: 'bold' }}>
                          {machineDowntimeData.reduce((sum, item) => sum + (item.total_occurrences || 0), 0)}
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#f3e5f5',
                        border: '2px solid #9c27b0',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOTAL DURATION</p>
                        <p style={{ margin: 0, color: '#9c27b0', fontSize: '28px', fontWeight: 'bold' }}>
                          {machineDowntimeData.reduce((sum, item) => sum + (item.total_seconds || 0), 0) > 0
                            ? (() => {
                                const totalSecs = machineDowntimeData.reduce((sum, item) => sum + (item.total_seconds || 0), 0);
                                const hours = Math.floor(totalSecs / 3600);
                                const mins = Math.floor((totalSecs % 3600) / 60);
                                return `${hours}h ${mins}m`;
                              })()
                            : '0h'}
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#e8f5e9',
                        border: '2px solid #4caf50',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>AVG DURATION</p>
                        <p style={{ margin: 0, color: '#4caf50', fontSize: '28px', fontWeight: 'bold' }}>
                          {machineDowntimeData.length > 0
                            ? (() => {
                                const avgSecs = Math.round(
                                  machineDowntimeData.reduce((sum, item) => sum + (item.total_seconds || 0), 0) /
                                  machineDowntimeData.reduce((sum, item) => sum + (item.total_occurrences || 0), 0)
                                );
                                const hours = Math.floor(avgSecs / 3600);
                                const mins = Math.floor((avgSecs % 3600) / 60);
                                return `${hours}h ${mins}m`;
                              })()
                            : '0h'}
                        </p>
                      </div>
                    </div>

                    {/* Pie Chart */}
                    <div style={{ marginBottom: '30px' }}>
                      <h4 style={{ fontSize: '14px', marginBottom: '10px' }}>Downtime Distribution by Reason</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={machineDowntimeData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={(entry: any) => `${entry.downtime_reason} (${entry.total_occurrences})`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="total_seconds"
                            nameKey="downtime_reason"
                          >
                            {machineDowntimeData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: any) => [
                              formatSecondsAsTime(Number(value)),
                              'Downtime',
                            ]}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Detailed Table */}
                    <div style={{ marginTop: '30px' }}>
                      <h4 style={{ fontSize: '14px', marginBottom: '10px' }}>Detailed Downtime Breakdown</h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '13px',
                          border: '1px solid #ddd',
                        }}>
                          <thead style={{ backgroundColor: '#f5f5f5' }}>
                            <tr>
                              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Downtime Reason</th>
                              <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Category</th>
                              <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Occurrences</th>
                              <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Total Duration</th>
                              <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Avg Duration</th>
                            </tr>
                          </thead>
                          <tbody>
                            {machineDowntimeData.map((item, idx) => {
                              const avgSecs = Math.round(item.total_seconds / item.total_occurrences);
                              const avgHours = Math.floor(avgSecs / 3600);
                              const avgMinutes = Math.floor((avgSecs % 3600) / 60);
                              const avgSec = Math.floor(avgSecs % 60);
                              const avgDuration = `${String(avgHours).padStart(2, '0')}:${String(avgMinutes).padStart(2, '0')}:${String(avgSec).padStart(2, '0')}`;
                              
                              const totalHours = Math.floor(item.total_seconds / 3600);
                              const totalMinutes = Math.floor((item.total_seconds % 3600) / 60);
                              const totalSec = Math.floor(item.total_seconds % 60);
                              const totalDuration = `${String(totalHours).padStart(2, '0')}:${String(totalMinutes).padStart(2, '0')}:${String(totalSec).padStart(2, '0')}`;

                              return (
                                <tr 
                                  key={idx} 
                                  style={{ 
                                    backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                  }}
                                  onClick={() => handleDowntimeRowClick(item)}
                                  onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '#e3f2fd';
                                    (e.currentTarget as HTMLTableRowElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLTableRowElement).style.backgroundColor = idx % 2 === 0 ? '#fafafa' : 'white';
                                    (e.currentTarget as HTMLTableRowElement).style.boxShadow = 'none';
                                  }}
                                >
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', fontWeight: '500' }}>
                                    {item.downtime_reason}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center' }}>
                                    <span style={{
                                      backgroundColor: item.category === 'mechanical' ? '#ff6b6b' : item.category === 'electrical' ? '#ffc107' : item.category === 'material' ? '#17a2b8' : '#999',
                                      color: item.category === 'electrical' ? '#333' : 'white',
                                      padding: '3px 8px',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      textTransform: 'capitalize',
                                    }}>
                                      {item.category}
                                    </span>
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold' }}>
                                    {item.total_occurrences}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold', color: '#d32f2f' }}>
                                    {totalDuration}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', color: '#1976d2' }}>
                                    {avgDuration}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-data">No downtime data available</div>
                )}
              </div>
            )}

            {/* PO TAB - Similar structure for other tabs */}
            {activeTab === 'po' && (
              <div className="chart-wrapper">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h3>No. of Copies Printed - Comprehensive Analytics</h3>
                  <div style={{display: 'flex', gap: '10px'}}>
                    <button 
                      onClick={handleViewSpreadsheetPrintOrders}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="View in spreadsheet"
                    >
                      📊 Spreadsheet
                    </button>
                    <button 
                      onClick={handleExportPrintOrders}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="Download as Excel"
                    >
                      <FileDownloadIcon style={{fontSize: '18px'}} /> Export
                    </button>
                  </div>
                </div>
                {printOrdersAnalytics && (printOrdersAnalytics.by_publication || []).length > 0 ? (
                  <div style={{ width: '100%' }}>
                    {/* Statistics Cards */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '15px',
                      marginBottom: '25px'
                    }}>
                      <div style={{
                        backgroundColor: '#e3f2fd',
                        border: '2px solid #2196F3',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOTAL COPIES PRINTED</p>
                        <p style={{ margin: 0, color: '#2196F3', fontSize: '28px', fontWeight: 'bold' }}>
                          {printOrdersAnalytics.statistics?.total_unique_pos || 0}
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#fff3e0',
                        border: '2px solid #ff9800',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOTAL PAGES PRINTED</p>
                        <p style={{ margin: 0, color: '#ff9800', fontSize: '28px', fontWeight: 'bold' }}>
                          {(printOrdersAnalytics.statistics?.total_pages || 0).toLocaleString()}
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#e8f5e9',
                        border: '2px solid #4caf50',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>MACHINES USED</p>
                        <p style={{ margin: 0, color: '#4caf50', fontSize: '28px', fontWeight: 'bold' }}>
                          {printOrdersAnalytics.statistics?.unique_machines || 0}
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#f3e5f5',
                        border: '2px solid #9c27b0',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>ALP PAGES</p>
                        <p style={{ margin: 0, color: '#9c27b0', fontSize: '28px', fontWeight: 'bold' }}>
                          {(printOrdersAnalytics.statistics?.total_alp || 0).toLocaleString()}
                        </p>
                        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '10px', fontStyle: 'italic' }}>
                          Σ(PO_i + waste_i) × pages_i
                        </p>
                      </div>
                    </div>

                    <MonthlyAlpChart records={printOrdersAnalytics.all_pos || []} />

                    <PrintOrderVarianceReport report={printOrdersAnalytics.variance_report} />

                    {/* POs by Machine Bar Chart */}
                    <CopyableImageBlock>
                      <div style={{ marginBottom: '30px', backgroundColor: '#fff', padding: '8px' }}>
                        <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#333' }}>No. of Copies Printed by Machine</h4>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={printOrdersAnalytics.by_machine || []} margin={{ top: 20, right: 30, left: 0, bottom: 80 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="machine_name" angle={-45} textAnchor="end" height={80} />
                            <YAxis yAxisId="left" allowDecimals={false} />
                            <YAxis yAxisId="right" orientation="right" allowDecimals={false} />
                            <Tooltip content={<CustomBarTooltip />} cursor={false} />
                            <Legend />
                            <Bar yAxisId="left" dataKey="total_pos" fill="#9c27b0" name="POs" isAnimationActive={false} shape={<CustomBarShape />} />
                            <Bar yAxisId="right" dataKey="total_pages" fill="#ffc658" name="Total Pages" isAnimationActive={false} shape={<CustomBarShape />} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CopyableImageBlock>

                    {/* Daily PO Trend Line Chart */}
                    {printOrdersAnalytics.daily_trend && printOrdersAnalytics.daily_trend.length > 0 && (
                      <CopyableImageBlock>
                        <div style={{ marginBottom: '30px', backgroundColor: '#fff', padding: '8px' }}>
                          <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#333' }}>Daily Copies Printed Trend</h4>
                          <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={printOrdersAnalytics.daily_trend || []}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="date" 
                                angle={-45}
                                textAnchor="end"
                                height={80}
                                interval={Math.max(0, Math.floor((printOrdersAnalytics.daily_trend?.length || 1) / 10) - 1)}
                              />
                              <YAxis yAxisId="left" allowDecimals={false} />
                              <YAxis yAxisId="right" orientation="right" allowDecimals={false} />
                              <Tooltip />
                              <Legend />
                              <Line yAxisId="left" type="monotone" dataKey="pos_created" stroke="#2196F3" name="POs Created" strokeWidth={2} />
                              <Line yAxisId="right" type="monotone" dataKey="total_pages" stroke="#ff9800" name="Total Pages" strokeWidth={2} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </CopyableImageBlock>
                    )}

                  </div>
                ) : (
                  <div className="no-data">No copies printed data available</div>
                )}
              </div>
            )}

            {/* DURATION TAB - schedule status by location */}
            {activeTab === 'duration' && (
              <div className="chart-wrapper schedule-status-report">
                <div className="schedule-status-title">
                  <div>MMCL ALL LOCATIONS PRINT START AND PRINT FINISH STATUS</div>
                  <div>{durationPeriodLabel}</div>
                </div>

                {scheduleStatusRows.length > 0 ? (
                  <div className="schedule-location-grid">
                    {scheduleStatusRows.map((locationRow: any, locationIndex: number) => (
                      <div
                        className={`schedule-location-card ${locationIndex % 2 === 0 ? 'schedule-location-card--blue' : ''}`}
                        key={locationRow.location}
                      >
                        <div className="schedule-location-name">{locationRow.location}</div>
                        <div className="schedule-metrics">
                          {SCHEDULE_STATUS_METRICS.map((metric) => {
                            const value = Number(locationRow[metric.key] || 0);
                            return (
                              <div className="schedule-metric-row" key={metric.key}>
                                <div className={`schedule-metric-label schedule-metric-label--${metric.tone}`}>
                                  {metric.label}
                                </div>
                                <div className="schedule-metric-count">{value}</div>
                                <div className="schedule-metric-bar-cell">
                                  <div
                                    className="schedule-metric-bar"
                                    style={{
                                      width: value > 0 ? `${Math.max(4, (value / scheduleStatusMax) * 100)}%` : '0%',
                                      backgroundColor: metric.color,
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-data schedule-status-empty">
                    No schedule comparison data is available. Add active schedule times in Admin &gt; Manage Publications.
                  </div>
                )}
              </div>
            )}

            {/* Previous duration analysis retained outside the active UI. */}
            {false && activeTab === 'duration' && (
              <div className="chart-wrapper">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h3>Print Duration - Hours Analysis</h3>
                  <div style={{display: 'flex', gap: '10px'}}>
                    <button 
                      onClick={handleViewSpreadsheetDuration}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="View in spreadsheet"
                    >
                      📊 Spreadsheet
                    </button>
                    <button 
                      onClick={handleExportDuration}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="Download as Excel"
                    >
                      <FileDownloadIcon style={{fontSize: '18px'}} /> Export
                    </button>
                  </div>
                </div>
                {printDurationAnalytics && (printDurationAnalytics.by_publication || []).length > 0 ? (
                  <div style={{ width: '100%' }}>
                    {/* Statistics Cards */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '15px',
                      marginBottom: '25px'
                    }}>
                      <div style={{
                        backgroundColor: '#e3f2fd',
                        border: '2px solid #2196F3',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOTAL MINUTES</p>
                        <p style={{ margin: 0, color: '#2196F3', fontSize: '28px', fontWeight: 'bold' }}>
                          {(parseFloat(printDurationAnalytics.statistics?.total_hours || 0) * 60).toFixed(0)}
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#fff3e0',
                        border: '2px solid #ff9800',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>AVG MINUTES</p>
                        <p style={{ margin: 0, color: '#ff9800', fontSize: '28px', fontWeight: 'bold' }}>
                          {(parseFloat(printDurationAnalytics.statistics?.avg_hours || 0) * 60).toFixed(0)}
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#e8f5e9',
                        border: '2px solid #4caf50',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>MIN MINUTES</p>
                        <p style={{ margin: 0, color: '#4caf50', fontSize: '28px', fontWeight: 'bold' }}>
                          {(parseFloat(printDurationAnalytics.statistics?.min_hours || 0) * 60).toFixed(0)}
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#fce4ec',
                        border: '2px solid #e91e63',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>MAX MINUTES</p>
                        <p style={{ margin: 0, color: '#e91e63', fontSize: '28px', fontWeight: 'bold' }}>
                          {(parseFloat(printDurationAnalytics.statistics?.max_hours || 0) * 60).toFixed(0)}
                        </p>
                      </div>
                    </div>

                    {/* Hours by Publication Bar Chart - Horizontal for 50+ publications */}
                    <div style={{ marginBottom: '30px' }}>
                      <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#333' }}>Print Duration by Publication</h4>
                      <ResponsiveContainer width="100%" height={Math.max(500, (printDurationAnalytics.by_publication?.length || 0) * 22)}>
                        <BarChart 
                          data={printDurationAnalytics.by_publication || []} 
                          layout="vertical" 
                          margin={{ top: 10, right: 40, left: -40, bottom: 10 }}
                          barSize={18}
                          barGap={6}
                          maxBarSize={18}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" allowDecimals={false} width={1} />
                          <YAxis 
                            dataKey="publication_name" 
                            type="category" 
                            width={175} 
                            tick={{ fontSize: 11, fill: '#333' }}
                            interval={0}
                          />
                          <Tooltip 
                            content={<CustomBarTooltip />} 
                            cursor={false}
                            wrapperStyle={{ outline: 'none' }}
                          />
                          <Legend 
                            wrapperStyle={{ paddingTop: '20px' }}
                            verticalAlign="bottom"
                            height={36}
                          />
                          <Bar 
                            dataKey="total_hours" 
                            fill="#2196F3" 
                            name="Total Minutes" 
                            isAnimationActive={false} 
                            shape={<CustomBarShape />}
                            radius={[0, 6, 6, 0]}
                          />
                          <Bar 
                            dataKey="avg_hours" 
                            fill="#ff9800" 
                            name="Avg Minutes" 
                            isAnimationActive={false} 
                            shape={<CustomBarShape />}
                            radius={[0, 6, 6, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Hours by Machine Bar Chart */}
                    <div style={{ marginBottom: '30px' }}>
                      <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#333' }}>Print Duration by Machine</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={printDurationAnalytics.by_machine || []} margin={{ top: 20, right: 30, left: 0, bottom: 80 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="machine_name" angle={-45} textAnchor="end" height={80} />
                          <YAxis yAxisId="left" allowDecimals={false} />
                          <YAxis yAxisId="right" orientation="right" allowDecimals={false} />
                          <Tooltip content={<CustomBarTooltip />} cursor={false} />
                          <Legend />
                          <Bar yAxisId="left" dataKey="total_hours" fill="#9c27b0" name="Total Hours" isAnimationActive={false} shape={<CustomBarShape />} />
                          <Bar yAxisId="right" dataKey="avg_hours" fill="#ffc658" name="Avg Hours" isAnimationActive={false} shape={<CustomBarShape />} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Daily Print Duration Trend Line Chart */}
                    {printDurationAnalytics.daily_trend && printDurationAnalytics.daily_trend.length > 0 && (
                      <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#333' }}>Daily Print Duration Trend</h4>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={printDurationAnalytics.daily_trend || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="date" 
                              angle={-45}
                              textAnchor="end"
                              height={80}
                              interval={Math.max(0, Math.floor((printDurationAnalytics.daily_trend?.length || 1) / 10) - 1)}
                            />
                            <YAxis yAxisId="left" allowDecimals={false} />
                            <YAxis yAxisId="right" orientation="right" allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Line yAxisId="left" type="monotone" dataKey="total_hours" stroke="#2196F3" name="Total Hours" strokeWidth={2} />
                            <Line yAxisId="right" type="monotone" dataKey="avg_hours" stroke="#ff9800" name="Avg Hours" strokeWidth={2} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Publication Summary Table */}
                    {printDurationAnalytics.by_publication && printDurationAnalytics.by_publication.length > 0 && (
                      <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#333' }}>Publication Duration Summary</h4>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '12px',
                            border: '1px solid #ddd',
                          }}>
                            <thead style={{ backgroundColor: '#f5f5f5' }}>
                              <tr>
                                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Publication</th>
                                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Total Minutes</th>
                                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Avg Minutes</th>
                                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Min Minutes</th>
                                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Max Minutes</th>
                                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Records</th>
                                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Machines</th>
                              </tr>
                            </thead>
                            <tbody>
                              {printDurationAnalytics.by_publication.map((pub: any, idx: number) => (
                                <tr key={idx} style={{
                                  backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white',
                                }}>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', fontWeight: '500' }}>
                                    {pub.publication_name || 'Unknown'}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold', color: '#2196F3' }}>
                                    {(parseFloat(pub.total_hours || 0) * 60).toFixed(0)}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', color: '#ff9800', fontWeight: 'bold' }}>
                                    {(parseFloat(pub.avg_hours || 0) * 60).toFixed(0)}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', color: '#4caf50' }}>
                                    {(parseFloat(pub.min_hours || 0) * 60).toFixed(0)}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', color: '#f44336', fontWeight: 'bold' }}>
                                    {(parseFloat(pub.max_hours || 0) * 60).toFixed(0)}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', color: '#666' }}>
                                    {pub.total_records}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold', color: '#9c27b0' }}>
                                    {pub.machines_used}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Machine Summary Table */}
                    {printDurationAnalytics.by_machine && printDurationAnalytics.by_machine.length > 0 && (
                      <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#333' }}>Machine Duration Summary</h4>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '12px',
                            border: '1px solid #ddd',
                          }}>
                            <thead style={{ backgroundColor: '#f5f5f5' }}>
                              <tr>
                                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Machine</th>
                                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Total Minutes</th>
                                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Avg Minutes</th>
                                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Min Minutes</th>
                                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Max Minutes</th>
                                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Records</th>
                                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Publications</th>
                              </tr>
                            </thead>
                            <tbody>
                              {printDurationAnalytics.by_machine.map((machine: any, idx: number) => (
                                <tr key={idx} style={{
                                  backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white',
                                }}>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', fontWeight: '500' }}>
                                    {machine.machine_name || 'Unknown'}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold', color: '#2196F3' }}>
                                    {(parseFloat(machine.total_hours || 0) * 60).toFixed(0)}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', color: '#ff9800', fontWeight: 'bold' }}>
                                    {(parseFloat(machine.avg_hours || 0) * 60).toFixed(0)}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', color: '#4caf50' }}>
                                    {(parseFloat(machine.min_hours || 0) * 60).toFixed(0)}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', color: '#f44336', fontWeight: 'bold' }}>
                                    {(parseFloat(machine.max_hours || 0) * 60).toFixed(0)}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', color: '#666' }}>
                                    {machine.total_records}
                                  </td>
                                  <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold', color: '#9c27b0' }}>
                                    {machine.publications_count}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-data">No print duration data available</div>
                )}
              </div>
            )}

            {/* LPRS TAB */}
            {activeTab === 'lprs' && (
              <div className="chart-wrapper">
                <h3>Last Page Received Second</h3>
                <div style={{ marginBottom: '28px' }}>
                  <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>
                    Fixed LPRS times are managed from Admin &gt; Manage Publications.
                  </p>
                </div>

                {lprsLocationRows.length > 0 && (
                  <div className="schedule-status-report" style={{ marginBottom: '24px' }}>
                    <div className="schedule-status-title">
                      <div>MMCL ALL LOCATIONS LPRS STATUS</div>
                      <div>{durationPeriodLabel}</div>
                    </div>
                    <div className="schedule-location-grid">
                      {lprsLocationRows.map((locationRow: any, locationIndex: number) => (
                        <div
                          className={`schedule-location-card ${locationIndex % 2 === 0 ? 'schedule-location-card--blue' : ''}`}
                          key={`lprs-${locationRow.location}`}
                        >
                          <div className="schedule-location-name">{locationRow.location}</div>
                          <div className="schedule-metrics">
                            {LPRS_OCCURRENCE_METRICS.map((metric) => {
                              const value = Number(locationRow[metric.key] || 0);
                              return (
                                <div className="schedule-metric-row" key={metric.key}>
                                  <div className={`schedule-metric-label schedule-metric-label--${metric.tone}`}>
                                    {metric.label}
                                  </div>
                                  <div className="schedule-metric-count">{value}</div>
                                  <div className="schedule-metric-bar-cell">
                                    <div
                                      className="schedule-metric-bar"
                                      style={{
                                        width: value > 0 ? `${Math.max(4, (value / lprsOccurrenceMax) * 100)}%` : '0%',
                                        backgroundColor: metric.color,
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h4 style={{ fontSize: '15px', margin: '0 0 12px 0', color: '#333' }}>LPRS Delay Report</h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '15px',
                    marginBottom: '18px',
                  }}>
                    <div style={{ backgroundColor: '#e3f2fd', border: '2px solid #2196F3', borderRadius: '8px', padding: '15px', textAlign: 'center' }}>
                      <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>RECORDS COMPARED</p>
                      <p style={{ margin: 0, color: '#2196F3', fontSize: '24px', fontWeight: 'bold' }}>{lprsDelayReport?.statistics?.total_records || 0}</p>
                    </div>
                    <div style={{ backgroundColor: '#ffebee', border: '2px solid #f44336', borderRadius: '8px', padding: '15px', textAlign: 'center' }}>
                      <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>DELAYED</p>
                      <p style={{ margin: 0, color: '#f44336', fontSize: '24px', fontWeight: 'bold' }}>{lprsDelayReport?.statistics?.delayed_records || 0}</p>
                    </div>
                    <div style={{ backgroundColor: '#e8f5e9', border: '2px solid #4caf50', borderRadius: '8px', padding: '15px', textAlign: 'center' }}>
                      <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>ON TIME</p>
                      <p style={{ margin: 0, color: '#4caf50', fontSize: '24px', fontWeight: 'bold' }}>{lprsDelayReport?.statistics?.on_time_records || 0}</p>
                    </div>
                    <div style={{ backgroundColor: '#eef7ff', border: '2px solid #03a9f4', borderRadius: '8px', padding: '15px', textAlign: 'center' }}>
                      <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>EARLY</p>
                      <p style={{ margin: 0, color: '#03a9f4', fontSize: '24px', fontWeight: 'bold' }}>{lprsDelayReport?.statistics?.early_records || 0}</p>
                    </div>
                    <div style={{ backgroundColor: '#fff3e0', border: '2px solid #ff9800', borderRadius: '8px', padding: '15px', textAlign: 'center' }}>
                      <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOTAL DELAY</p>
                      <p style={{ margin: 0, color: '#ff9800', fontSize: '24px', fontWeight: 'bold' }}>{lprsDelayReport?.statistics?.total_delay_time || '00:00:00'}</p>
                    </div>
                  </div>

                  {lprsConsolidatedRows.length > 0 && (
                    <div style={{
                      marginBottom: '24px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '16px',
                      backgroundColor: '#fff',
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'wrap',
                        marginBottom: '14px',
                      }}>
                        <h4 style={{ fontSize: '15px', margin: 0, color: '#333' }}>Consolidated LPRS by Publication</h4>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {(Object.keys(LPRS_STATUS_CONFIG) as LprsStatus[]).map((status) => {
                            const config = LPRS_STATUS_CONFIG[status];
                            const isSelected = selectedLprsStatus === status;
                            const totalRecords = lprsConsolidatedRows.reduce((sum: number, row: any) => sum + Number(row[config.recordsKey] || 0), 0);
                            return (
                              <button
                                key={status}
                                type="button"
                                onClick={() => setSelectedLprsStatus(status)}
                                style={{
                                  padding: '8px 12px',
                                  border: `2px solid ${config.color}`,
                                  borderRadius: '6px',
                                  backgroundColor: isSelected ? config.color : '#fff',
                                  color: isSelected ? '#fff' : config.color,
                                  cursor: 'pointer',
                                  fontWeight: 700,
                                  fontSize: '12px',
                                }}
                              >
                                {config.label}: {totalRecords}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={lprsConsolidatedRows} margin={{ top: 10, right: 25, left: 0, bottom: 70 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="publication_name"
                            angle={-35}
                            textAnchor="end"
                            interval={0}
                            height={85}
                            tick={{ fontSize: 11 }}
                          />
                          <YAxis allowDecimals={false} />
                          <Tooltip
                            formatter={(value: any, name: any) => [value, name]}
                            labelFormatter={(label) => `Publication: ${label}`}
                          />
                          <Legend
                            onClick={(payload: any) => {
                              if (payload?.value === 'Delayed') setSelectedLprsStatus('Delayed');
                              if (payload?.value === 'Early') setSelectedLprsStatus('Early');
                              if (payload?.value === 'On time') setSelectedLprsStatus('On time');
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="delayed_records"
                            name="Delayed"
                            stroke={LPRS_STATUS_CONFIG.Delayed.color}
                            strokeWidth={selectedLprsStatus === 'Delayed' ? 4 : 2}
                            dot={{ r: selectedLprsStatus === 'Delayed' ? 5 : 3 }}
                            activeDot={{ r: 7, onClick: () => setSelectedLprsStatus('Delayed') }}
                            onClick={() => setSelectedLprsStatus('Delayed')}
                          />
                          <Line
                            type="monotone"
                            dataKey="early_records"
                            name="Early"
                            stroke={LPRS_STATUS_CONFIG.Early.color}
                            strokeWidth={selectedLprsStatus === 'Early' ? 4 : 2}
                            dot={{ r: selectedLprsStatus === 'Early' ? 5 : 3 }}
                            activeDot={{ r: 7, onClick: () => setSelectedLprsStatus('Early') }}
                            onClick={() => setSelectedLprsStatus('Early')}
                          />
                          <Line
                            type="monotone"
                            dataKey="on_time_records"
                            name="On time"
                            stroke={LPRS_STATUS_CONFIG['On time'].color}
                            strokeWidth={selectedLprsStatus === 'On time' ? 4 : 2}
                            dot={{ r: selectedLprsStatus === 'On time' ? 5 : 3 }}
                            activeDot={{ r: 7, onClick: () => setSelectedLprsStatus('On time') }}
                            onClick={() => setSelectedLprsStatus('On time')}
                          />
                        </LineChart>
                      </ResponsiveContainer>

                      <div style={{ overflowX: 'auto', marginTop: '12px' }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '12px',
                          border: '1px solid #ddd',
                          backgroundColor: '#fff',
                        }}>
                          <thead style={{ backgroundColor: '#f5f5f5' }}>
                            <tr>
                              <th style={{ padding: '9px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Publication</th>
                              <th style={{ padding: '9px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>{selectedLprsStatus} Records</th>
                              <th style={{ padding: '9px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Average LPRS Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedLprsRows.length > 0 ? selectedLprsRows.map((row: any) => (
                              <tr key={`${selectedLprsStatus}-${row.publication_name}`}>
                                <td style={{ padding: '9px', borderBottom: '1px solid #ddd', fontWeight: 700 }}>{row.publication_name}</td>
                                <td style={{ padding: '9px', borderBottom: '1px solid #ddd', textAlign: 'center' }}>{row[selectedLprsConfig.recordsKey]}</td>
                                <td style={{
                                  padding: '9px',
                                  borderBottom: '1px solid #ddd',
                                  textAlign: 'center',
                                  color: selectedLprsConfig.color,
                                  fontWeight: 700,
                                }}>
                                  {formatSecondsAsTime(
                                    Number(row[selectedLprsConfig.lprsSecondsKey] || 0)
                                      / Number(row[selectedLprsConfig.recordsKey] || 1)
                                  )}
                                </td>
                              </tr>
                            )) : (
                              <tr>
                                <td colSpan={3} style={{ padding: '12px', textAlign: 'center', color: '#777' }}>
                                  No publications found for {selectedLprsStatus}.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '12px',
                      border: '1px solid #ddd',
                      backgroundColor: '#fff',
                    }}>
                      <thead style={{ backgroundColor: '#f5f5f5' }}>
                        <tr>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Date</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Publication</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Machine</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>No. of Copies Printed</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Scheduled</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Actual</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Difference</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(lprsDelayReport?.data || []).length > 0 ? lprsDelayReport.data.map((row: any, idx: number) => (
                          <tr key={row.id} style={{ backgroundColor: idx % 2 === 0 ? '#fafafa' : '#fff' }}>
                            <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{formatDate(row.date)}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #ddd', fontWeight: 600 }}>{row.publication_name}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{row.machine_name || '-'}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center' }}>{row.po_number}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center' }}>{row.scheduled_lprs_time}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center' }}>{row.actual_lprs_time}</td>
                            <td style={{
                              padding: '10px',
                              borderBottom: '1px solid #ddd',
                              textAlign: 'center',
                              fontWeight: 700,
                              color: row.status === 'Delayed' ? '#f44336' : row.status === 'Early' ? '#03a9f4' : '#4caf50',
                            }}>
                              {row.status === 'Delayed' ? '+' : row.status === 'Early' ? '-' : ''}{row.variance_time || row.delay_time}
                            </td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center' }}>
                              {row.status}
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan={8} style={{ padding: '14px', textAlign: 'center', color: '#777' }}>
                              No LPRS delay data available. Add active scheduled times above to compare records.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* MACHINE TAB */}
            {activeTab === 'machine' && (
              <div className="chart-wrapper">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h3>Machine Usage - Detailed Analysis</h3>
                  <div style={{display: 'flex', gap: '10px'}}>
                    <button 
                      onClick={handleViewSpreadsheetMachine}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="View in spreadsheet"
                    >
                      📊 Spreadsheet
                    </button>
                    <button 
                      onClick={handleExportMachine}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="Download as Excel"
                    >
                      <FileDownloadIcon style={{fontSize: '18px'}} /> Export
                    </button>
                  </div>
                </div>
                {machineAnalytics && (machineAnalytics.by_machine || []).length > 0 ? (
                  <div style={{ width: '100%' }}>
                    {/* Statistics Cards */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                      gap: '15px',
                      marginBottom: '25px'
                    }}>
                      <div style={{
                        backgroundColor: '#e3f2fd',
                        border: '2px solid #2196F3',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOTAL MACHINES</p>
                        <p style={{ margin: 0, color: '#2196F3', fontSize: '24px', fontWeight: 'bold' }}>
                          {machineAnalytics.statistics?.total_machines || 0}
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#fff3e0',
                        border: '2px solid #ff9800',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOTAL PLATES</p>
                        <p style={{ margin: 0, color: '#ff9800', fontSize: '24px', fontWeight: 'bold' }}>
                          {machineAnalytics.statistics?.total_plates || 0}
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#f3e5f5',
                        border: '2px solid #9c27b0',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOTAL PAGES</p>
                        <p style={{ margin: 0, color: '#9c27b0', fontSize: '24px', fontWeight: 'bold' }}>
                          {machineAnalytics.statistics?.total_pages || 0}
                        </p>
                      </div>
                    </div>

                    {/* Machine Usage Pie Chart */}
                    <div style={{ marginBottom: '30px' }}>
                      <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#333' }}>Machine Plates Distribution</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={machineAnalytics.by_machine || []}
                            dataKey="total_plates"
                            nameKey="machine_name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                          >
                            {(machineAnalytics.by_machine || []).map((entry: any, index: number) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: any) => `${value} plates`} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Machine Comparison Bar Chart */}
                    <div style={{ marginBottom: '30px' }}>
                      <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#333' }}>Machine Plates Comparison</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={machineAnalytics.by_machine || []} margin={{ top: 20, right: 30, left: 0, bottom: 80 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="machine_name" angle={-45} textAnchor="end" height={80} />
                          <YAxis yAxisId="left" allowDecimals={false} />
                          <YAxis yAxisId="right" orientation="right" allowDecimals={false} />
                          <Tooltip content={<CustomBarTooltip />} cursor={false} />
                          <Legend />
                          <Bar yAxisId="left" dataKey="total_pages" fill="#2196F3" name="Pages" isAnimationActive={false} shape={<CustomBarShape />} />
                          <Bar yAxisId="right" dataKey="total_plates" fill="#ff9800" name="Plates" isAnimationActive={false} shape={<CustomBarShape />} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Daily Trend Line Chart */}
                    <div style={{ marginBottom: '30px' }}>
                      <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#333' }}>Daily Machine Plates Trend</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={machineAnalytics.daily_trend || []}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="total_plates" stroke="#ff9800" name="Plates" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="no-data">No machine usage data available</div>
                )}
              </div>
            )}

            {/* PLATE TAB */}
            {activeTab === 'plate' && (
              <div className="chart-wrapper">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h3>Plate Consumption Analysis</h3>
                  <div style={{display: 'flex', gap: '10px'}}>
                    <button 
                      onClick={handleViewSpreadsheetPlate}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="View in spreadsheet"
                    >
                      📊 Spreadsheet
                    </button>
                    <button 
                      onClick={handleExportPlate}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="Download as Excel"
                    >
                      <FileDownloadIcon style={{fontSize: '18px'}} /> Export
                    </button>
                  </div>
                </div>
                {plateConsumptionAnalytics && (plateConsumptionAnalytics.daily_trend || []).length > 0 ? (
                  <div style={{ width: '100%' }}>
                    {/* Statistics Cards */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '15px',
                      marginBottom: '25px'
                    }}>
                      <div style={{
                        backgroundColor: '#fff3e0',
                        border: '2px solid #ff9800',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOTAL PLATES</p>
                        <p style={{ margin: 0, color: '#ff9800', fontSize: '24px', fontWeight: 'bold' }}>
                          {plateConsumptionAnalytics.statistics?.total_plates || 0}
                        </p>
                      </div>
                    </div>

                    <PlateConsumptionTotalChart monthlyRows={plateConsumptionAnalytics.monthly_consumption || []} />
                    <PlateConsumptionMonthlyChart
                      data={plateConsumptionAnalytics.monthly_by_location || []}
                      totalPlates={plateConsumptionAnalytics.statistics?.total_plates || 0}
                      startDate={filters.startDate}
                      endDate={filters.endDate}
                      label={filters.location.length > 0 ? `MMCL-${filters.location.join(', ')}` : 'MMCL-All'}
                    />
                  </div>
                ) : (
                  <div className="no-data">No plate consumption data available</div>
                )}
              </div>
            )}

            {/* NEWSPRINT TAB */}
            {activeTab === 'newsprint' && (
              <div className="chart-wrapper">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h3>Newsprint KGs Analysis</h3>
                  <div style={{display: 'flex', gap: '10px'}}>
                    <button 
                      onClick={handleViewSpreadsheetNewsprint}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="View in spreadsheet"
                    >
                      📊 Spreadsheet
                    </button>
                    <button 
                      onClick={handleExportNewsprint}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="Download as Excel"
                    >
                      <FileDownloadIcon style={{fontSize: '18px'}} /> Export
                    </button>
                  </div>
                </div>
                {newsprintAnalytics && (newsprintAnalytics.daily_trend || []).length > 0 ? (
                  <div style={{ width: '100%' }}>
                    {/* Statistics Cards */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '15px',
                      marginBottom: '25px'
                    }}>
                      <div style={{
                        backgroundColor: '#fff3e0',
                        border: '2px solid #ff9800',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOTAL KGs</p>
                        <p style={{ margin: 0, color: '#ff9800', fontSize: '24px', fontWeight: 'bold' }}>
                          {newsprintAnalytics.statistics?.total_kgs || 0}
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#e3f2fd',
                        border: '2px solid #2196F3',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>ALP PAGES</p>
                        <p style={{ margin: 0, color: '#2196F3', fontSize: '24px', fontWeight: 'bold' }}>
                          {(newsprintAnalytics.statistics?.total_alp || 0).toLocaleString()}
                        </p>
                        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '10px', fontStyle: 'italic' }}>
                          Σ(PO_i + waste_i) × pages_i
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#f3e5f5',
                        border: '2px solid #9c27b0',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>AVG YIELD</p>
                        <p style={{ margin: 0, color: '#9c27b0', fontSize: '24px', fontWeight: 'bold' }}>
                          {Math.round(Number(newsprintAnalytics.statistics?.avg_yield || 0)).toLocaleString()}
                        </p>
                        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '10px', fontStyle: 'italic' }}>
                          Total ALP / Total KGs
                        </p>
                      </div>
                    </div>

                    <NewsprintConsumptionChart monthlyRows={newsprintAnalytics.monthly_consumption || []} />
                    <NewsprintConsumptionByLocationChart
                      data={newsprintAnalytics.monthly_by_location || []}
                      totalKgs={newsprintAnalytics.statistics?.total_kgs || 0}
                      startDate={filters.startDate}
                      endDate={filters.endDate}
                      label={filters.location.length > 0 ? `MMCL-${filters.location.join(', ')}` : 'MMCL-All'}
                    />
                  </div>
                ) : (
                  <div className="no-data">No newsprint data available</div>
                )}
              </div>
            )}

            {/* WASTES TAB */}
            {activeTab === 'wastes' && (
              <div className="chart-wrapper">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h3>Waste Categories Analysis - Newsprint & Plates</h3>
                  <div style={{display: 'flex', gap: '10px'}}>
                    <button 
                      onClick={() => {
                        // Handle spreadsheet view for wastes
                        const wastesSpreadsheetData: SpreadsheetData = {
                          title: 'Waste Categories Analysis',
                          columns: [
                            { key: 'date', header: 'Date', width: 100 },
                            { key: 'machine_name', header: 'Machine', width: 120 },
                            { key: 'waste_category', header: 'Category', width: 140 },
                            { key: 'total_quantity', header: 'Quantity', width: 100 }
                          ],
                          rows: wastesAnalytics ? [...(wastesAnalytics.newsprint_wastes || []), ...(wastesAnalytics.plates_wastes || [])] : []
                        };
                        handleOpenSpreadsheet(wastesSpreadsheetData, () => handleExportWastes());
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="View in spreadsheet"
                    >
                      📊 Spreadsheet
                    </button>
                    <button 
                      onClick={handleExportWastes}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="Download as Excel"
                    >
                      <FileDownloadIcon style={{fontSize: '18px'}} /> Export
                    </button>
                  </div>
                </div>
                {wastesAnalytics ? (
                  <div>
                    <WasteGeneratedChart
                      monthlyRows={wastesAnalytics.monthly_total_waste_generated || []}
                      title="Total Waste Generated"
                      seriesLabel="Total Waste"
                      noDataText="No total waste generated data available"
                      wasteKey="total_waste_kgs"
                    />

                    {/* ===== MONTH-ON-MONTH WASTE PERCENTAGE BY LOCATION & MACHINE ===== */}
                    <div style={{ marginBottom: '60px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '2px solid #ddd' }}>
                      <h4 style={{ fontSize: '16px', marginBottom: '20px', color: '#333', fontWeight: 'bold' }}>Waste Trend - Month on Month Comparison with Machines</h4>
                      <WastePercentComparisonSection
                        data={wastesAnalytics.monthly_waste_percent_by_machine_location || []}
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                      />
                    </div>

                    {/* ===== WASTE SECTION TOGGLES ===== */}
                    <div style={{ marginBottom: '40px', display: 'flex', gap: '15px', justifyContent: 'center' }}>
                      <button
                        onClick={() => {
                          setShowNewsprintWaste(true);
                          setShowPlatesWaste(false);
                        }}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: showNewsprintWaste ? '#2196F3' : '#e0e0e0',
                          color: showNewsprintWaste ? 'white' : '#333',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          transition: 'all 0.3s',
                          boxShadow: showNewsprintWaste ? '0 4px 12px rgba(33, 150, 243, 0.3)' : 'none'
                        }}
                      >
                        📄 Newsprint Waste Analysis
                      </button>
                      <button
                        onClick={() => {
                          setShowNewsprintWaste(false);
                          setShowPlatesWaste(true);
                        }}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: showPlatesWaste ? '#E91E63' : '#e0e0e0',
                          color: showPlatesWaste ? 'white' : '#333',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          transition: 'all 0.3s',
                          boxShadow: showPlatesWaste ? '0 4px 12px rgba(233, 30, 99, 0.3)' : 'none'
                        }}
                      >
                        📋 Plates Waste Analysis
                      </button>
                    </div>

                    {/* ===== NEWSPRINT WASTE SECTION ===== */}
                    {showNewsprintWaste && (
                    <div style={{ marginBottom: '60px', borderTop: '3px solid #2196F3', paddingTop: '30px' }}>
                      <h4 style={{ fontSize: '18px', marginBottom: '25px', color: '#1976D2', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        Newsprint Waste Analysis
                      </h4>
                      
                      {/* Summary Cards */}
                      {wastesAnalytics.newsprint_wastes && wastesAnalytics.newsprint_wastes.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '25px' }}>
                          <div style={{ padding: '20px', backgroundColor: '#E3F2FD', borderRadius: '8px', borderLeft: '4px solid #2196F3' }}>
                            <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', textTransform: 'uppercase', fontWeight: '600' }}>Total Quantity</p>
                            <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#1976D2' }}>
                              {wastesAnalytics.newsprint_wastes.reduce((sum: number, w: any) => sum + (parseInt(w.total_quantity) || 0), 0).toLocaleString()}
                            </p>
                          </div>
                          <div style={{ padding: '20px', backgroundColor: '#E3F2FD', borderRadius: '8px', borderLeft: '4px solid #1976D2' }}>
                            <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', textTransform: 'uppercase', fontWeight: '600' }}>Total Weight (kg)</p>
                            <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#1976D2' }}>
                              {wastesAnalytics.newsprint_wastes.reduce((sum: number, w: any) => sum + (parseFloat(w.total_weight) || 0), 0).toFixed(2)}
                            </p>
                          </div>
                          <div style={{ padding: '20px', backgroundColor: '#E3F2FD', borderRadius: '8px', borderLeft: '4px solid #42A5F5' }}>
                            <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', textTransform: 'uppercase', fontWeight: '600' }}>Categories</p>
                            <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#2196F3' }}>
                              {new Set(wastesAnalytics.newsprint_wastes.map((w: any) => w.waste_category)).size}
                            </p>
                          </div>
                        </div>
                      )}

                      <div style={{ marginBottom: '26px' }}>
                        <h5 style={{ margin: '0 0 14px 0', color: '#333', fontSize: '15px', fontWeight: 700 }}>
                          Print Waste Generated Analysis Overview
                        </h5>
                        <WasteGeneratedChart
                          monthlyRows={wastesAnalytics.monthly_print_waste_generated || []}
                          title="Print Waste Generated"
                          seriesLabel="Print Waste"
                          noDataText="No print waste generated data available"
                          wasteKey="total_print_waste_kgs"
                        />
                      </div>

                      {/* Filter Dropdown */}
                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ marginRight: '10px', fontWeight: 'bold', color: '#333' }}>📋 Filter by Category:</label>
                        <select
                          value={selectedNewsprintWaste}
                          onChange={(e) => setSelectedNewsprintWaste(e.target.value)}
                          style={{
                            padding: '10px 12px',
                            borderRadius: '6px',
                            border: '2px solid #2196F3',
                            backgroundColor: '#ffffff',
                            color: '#333',
                            fontWeight: '500',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          {wastesAnalytics.newsprint_wastes && [...new Set(wastesAnalytics.newsprint_wastes.map((w: any) => w.waste_category))]
                            .sort((a: any, b: any) => {
                              if (String(a).toLowerCase() === 'print waste') return -1;
                              if (String(b).toLowerCase() === 'print waste') return 1;
                              return String(a).localeCompare(String(b));
                            })
                            .map((cat: any) => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                      </div>

                      <NewsprintCategoryTrendSection
                        data={wastesAnalytics.monthly_newsprint_category_percent || []}
                        category={selectedNewsprintWaste}
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                      />

                      {/* Trend Line Chart */}
                      {wastesAnalytics.newsprint_wastes && wastesAnalytics.newsprint_wastes.length > 0 && (
                        <div style={{ marginBottom: '30px', backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px' }}>
                          <h5 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '14px', fontWeight: '600' }}>Trend Over Time</h5>
                          <ResponsiveContainer width="100%" height={450}>
                            <LineChart
                              data={
                                selectedNewsprintWaste === 'ALL'
                                  ? (() => {
                                      const aggregated: { [key: string]: any } = {};
                                      wastesAnalytics.newsprint_wastes.forEach((w: any) => {
                                        if (!aggregated[w.date]) {
                                          aggregated[w.date] = { date: w.date, total_quantity: 0, total_weight: 0 };
                                        }
                                        aggregated[w.date].total_quantity += parseInt(w.total_quantity || 0);
                                        aggregated[w.date].total_weight += parseFloat(w.total_weight || 0);
                                      });
                                      return Object.values(aggregated).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
                                    })()
                                  : wastesAnalytics.newsprint_wastes.filter((w: any) => w.waste_category === selectedNewsprintWaste).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
                              }
                              margin={{ top: 50, right: 50, left: 10, bottom: 60 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                              <XAxis dataKey="date" angle={-45} textAnchor="end" height={100} tick={{ fontSize: 11, fill: '#666' }} />
                              <YAxis tick={{ fontSize: 11, fill: '#666' }} />
                              <Tooltip 
                                contentStyle={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                  border: '2px solid #2196F3',
                                  borderRadius: '8px',
                                  padding: '12px 16px',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
                                }}
                                wrapperStyle={{ outline: 'none' }} 
                                isAnimationActive={true} 
                                cursor={true} 
                              />
                              <Legend wrapperStyle={{ paddingTop: '20px' }} />
                              <Line type="monotone" dataKey="total_quantity" stroke="#2196F3" strokeWidth={2} dot={{ r: 4, fill: '#2196F3' }} activeDot={{ r: 7, fill: '#1976D2' }} name="Pages Wasted" />
                              <Line type="monotone" dataKey="total_weight" stroke="#e7cc1cff" strokeWidth={2} dot={{ r: 4, fill: '#1976D2' }} activeDot={{ r: 7, fill: '#0D47A1' }} name="Weight (kg)" />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* Bar Chart Comparison */}
                      {wastesAnalytics.newsprint_wastes && wastesAnalytics.newsprint_wastes.length > 0 && (
                        <div style={{ marginBottom: '30px', backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px' }}>
                          <h5 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '14px', fontWeight: '600' }}>Daily Comparison</h5>
                          <ResponsiveContainer width="100%" height={450}>
                            <BarChart
                              data={
                                selectedNewsprintWaste === 'ALL'
                                  ? (() => {
                                      const aggregated: { [key: string]: any } = {};
                                      wastesAnalytics.newsprint_wastes.forEach((w: any) => {
                                        if (!aggregated[w.date]) {
                                          aggregated[w.date] = { date: w.date, total_quantity: 0, total_weight: 0 };
                                        }
                                        aggregated[w.date].total_quantity += parseInt(w.total_quantity || 0);
                                        aggregated[w.date].total_weight += parseFloat(w.total_weight || 0);
                                      });
                                      return Object.values(aggregated).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
                                    })()
                                  : wastesAnalytics.newsprint_wastes.filter((w: any) => w.waste_category === selectedNewsprintWaste).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
                              }
                              margin={{ top: 50, right: 50, left: 10, bottom: 60 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                              <XAxis dataKey="date" angle={-45} textAnchor="end" height={100} tick={{ fontSize: 11, fill: '#666' }} />
                              <YAxis tick={{ fontSize: 11, fill: '#666' }} />
                              <Tooltip content={<CustomBarTooltip />} cursor={false} />
                              <Legend wrapperStyle={{ paddingTop: '20px' }} />
                              <Bar dataKey="total_quantity" fill="#42A5F5" name="Pages Wasted" isAnimationActive={false} shape={<CustomBarShape />} />
                              <Bar dataKey="total_weight" fill="#f3bb21ff" name="Weight (kg)" isAnimationActive={false} shape={<CustomBarShape />} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                    </div>
                    )}

                    {/* ===== PLATES WASTE SECTION ===== */}
                    {showPlatesWaste && (
                    <div style={{ marginBottom: '60px', borderTop: '3px solid #E91E63', paddingTop: '30px' }}>
                      <h4 style={{ fontSize: '18px', marginBottom: '25px', color: '#C2185B', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        🔧 Plates Waste Analysis
                      </h4>
                      
                      {/* Summary Cards */}
                      {wastesAnalytics.plates_wastes && wastesAnalytics.plates_wastes.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '25px' }}>
                          <div style={{ padding: '20px', backgroundColor: '#FCE4EC', borderRadius: '8px', borderLeft: '4px solid #E91E63' }}>
                            <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', textTransform: 'uppercase', fontWeight: '600' }}>Total Quantity</p>
                            <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#C2185B' }}>
                              {wastesAnalytics.plates_wastes.reduce((sum: number, w: any) => sum + (parseInt(w.total_quantity) || 0), 0).toLocaleString()}
                            </p>
                          </div>
                          <div style={{ padding: '20px', backgroundColor: '#FCE4EC', borderRadius: '8px', borderLeft: '4px solid #E91E63' }}>
                            <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', textTransform: 'uppercase', fontWeight: '600' }}>Categories</p>
                            <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#C2185B' }}>
                              {new Set(wastesAnalytics.plates_wastes.map((w: any) => w.waste_category)).size}
                            </p>
                          </div>
                          <div style={{ padding: '20px', backgroundColor: '#FCE4EC', borderRadius: '8px', borderLeft: '4px solid #EC407A' }}>
                            <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', textTransform: 'uppercase', fontWeight: '600' }}>Total Records</p>
                            <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#E91E63' }}>
                              {wastesAnalytics.plates_wastes.reduce((sum: number, w: any) => sum + (parseInt(w.total_records) || 0), 0).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Filter Dropdown */}
                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ marginRight: '10px', fontWeight: 'bold', color: '#333' }}>📋 Filter by Category:</label>
                        <select
                          value={selectedPlatesWaste}
                          onChange={(e) => setSelectedPlatesWaste(e.target.value)}
                          style={{
                            padding: '10px 12px',
                            borderRadius: '6px',
                            border: '2px solid #E91E63',
                            backgroundColor: '#ffffff',
                            color: '#333',
                            fontWeight: '500',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          <option value="ALL">✓ All Categories</option>
                          {wastesAnalytics.plates_wastes && [...new Set(wastesAnalytics.plates_wastes.map((w: any) => w.waste_category))].map((cat: any) => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>

                      {/* Trend Line Chart */}
                      {wastesAnalytics.plates_wastes && wastesAnalytics.plates_wastes.length > 0 && (
                        <div style={{ marginBottom: '30px', backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px' }}>
                          <h5 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '14px', fontWeight: '600' }}>Trend Over Time</h5>
                          <ResponsiveContainer width="100%" height={450}>
                            <LineChart
                              data={
                                selectedPlatesWaste === 'ALL'
                                  ? (() => {
                                      const aggregated: { [key: string]: any } = {};
                                      wastesAnalytics.plates_wastes.forEach((w: any) => {
                                        if (!aggregated[w.date]) {
                                          aggregated[w.date] = { date: w.date, total_quantity: 0 };
                                        }
                                        aggregated[w.date].total_quantity += parseInt(w.total_quantity || 0);
                                      });
                                      return Object.values(aggregated).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
                                    })()
                                  : wastesAnalytics.plates_wastes.filter((w: any) => w.waste_category === selectedPlatesWaste).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
                              }
                              margin={{ top: 50, right: 50, left: 10, bottom: 60 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                              <XAxis dataKey="date" angle={-45} textAnchor="end" height={100} tick={{ fontSize: 11, fill: '#666' }} />
                              <YAxis tick={{ fontSize: 11, fill: '#666' }} />
                              <Tooltip 
                                contentStyle={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                  border: '2px solid #E91E63',
                                  borderRadius: '8px',
                                  padding: '12px 16px',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
                                }}
                                wrapperStyle={{ outline: 'none' }} 
                                isAnimationActive={true} 
                                cursor={true} 
                              />
                              <Legend wrapperStyle={{ paddingTop: '20px' }} />
                              <Line type="monotone" dataKey="total_quantity" stroke="#E91E63" strokeWidth={2} dot={{ r: 4, fill: '#E91E63' }} activeDot={{ r: 7, fill: '#C2185B' }} name="Plates wasted" />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* Bar Chart Comparison */}
                      {wastesAnalytics.plates_wastes && wastesAnalytics.plates_wastes.length > 0 && (
                        <div style={{ marginBottom: '30px', backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px' }}>
                          <h5 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '14px', fontWeight: '600' }}>Daily Comparison</h5>
                          <ResponsiveContainer width="100%" height={450}>
                            <BarChart
                              data={
                                selectedPlatesWaste === 'ALL'
                                  ? (() => {
                                      const aggregated: { [key: string]: any } = {};
                                      wastesAnalytics.plates_wastes.forEach((w: any) => {
                                        if (!aggregated[w.date]) {
                                          aggregated[w.date] = { date: w.date, total_quantity: 0 };
                                        }
                                        aggregated[w.date].total_quantity += parseInt(w.total_quantity || 0);
                                      });
                                      return Object.values(aggregated).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
                                    })()
                                  : wastesAnalytics.plates_wastes.filter((w: any) => w.waste_category === selectedPlatesWaste).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
                              }
                              margin={{ top: 50, right: 50, left: 10, bottom: 60 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                              <XAxis dataKey="date" angle={-45} textAnchor="end" height={100} tick={{ fontSize: 11, fill: '#666' }} />
                              <YAxis tick={{ fontSize: 11, fill: '#666' }} />
                              <Tooltip content={<CustomBarTooltip />} cursor={false} />
                              <Legend wrapperStyle={{ paddingTop: '20px' }} />
                              <Bar dataKey="total_quantity" fill="#EC407A" name="Plates Wasted" isAnimationActive={false} shape={<CustomBarShape />} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                    </div>
                    )}

                   
                  </div>
                ) : (
                  <p style={{ color: '#999', fontStyle: 'italic' }}>Loading waste analysis...</p>
                )}
              </div>
            )}

            {/* TOWERS TAB */}
            {activeTab === 'towers' && (
              <div className="chart-wrapper">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h3>📍 Production Analysis by Towers</h3>
                  <div style={{display: 'flex', gap: '10px'}}>
                    <button 
                      onClick={handleViewSpreadsheetTowers}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="View in spreadsheet"
                    >
                      📊 Spreadsheet
                    </button>
                    <button 
                      onClick={handleExportTowers}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="Download as Excel"
                    >
                      <FileDownloadIcon style={{fontSize: '18px'}} /> Export
                    </button>
                  </div>
                </div>
                <p style={{ fontSize: '12px', color: '#666', marginBottom: '20px' }}>
                  View all production records grouped and analyzed by towers. Filter data is applied from date range, publication, machine, and location selections.
                </p>
                {isLoading ? (
                  <p style={{ color: '#999', fontStyle: 'italic' }}>Loading tower analysis data...</p>
                ) : towerRecordsData && towerRecordsData.length > 0 ? (
                  <div style={{ width: '100%' }}>
                   
                    {/* Tower Summary Cards - Show PO Numbers */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '15px',
                      marginBottom: '25px'
                    }}>
                      {[1, 2, 3, 4, 5].map((towerId: number) => {
                        const towerRecords = towerRecordsData.filter((r: any) => 
                          r.tower_ids && Array.isArray(r.tower_ids) && r.tower_ids.includes(towerId)
                        );
                        const towerPages = towerRecords.reduce((sum: number, r: any) => sum + (r.total_pages || 0), 0);
                        // Sum all PO numbers for this tower
                        const totalPoValue = towerRecords.reduce((sum: number, r: any) => sum + (parseInt(r.po_number) || 0), 0);
                        
                        return (
                          <div key={towerId} style={{
                            backgroundColor: '#f0f7ff',
                            border: '2px solid #2196F3',
                            borderRadius: '8px',
                            padding: '15px',
                            textAlign: 'center'
                          }}>
                            <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOWER {towerId}</p>
                            <p style={{ margin: '0 0 12px 0', color: '#1976D2', fontSize: '24px', fontWeight: 'bold' }}>
                              {totalPoValue.toLocaleString()}
                            </p>
                            <p style={{ margin: '0', color: '#999', fontSize: '10px' }}>
                              {towerPages.toLocaleString()} pages
                            </p>
                          </div>
                        );
                      })}
                    </div>

                    {/* Towers Data Table */}
                    <div style={{ marginBottom: '30px' }}>
                      <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#333' }}>Production Records by Tower</h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          fontSize: '12px'
                        }}>
                          <thead style={{ backgroundColor: '#2196F3' }}>
                            <tr>
                              <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: 'bold', borderBottom: '2px solid #1976D2' }}>Date</th>
                              <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: 'bold', borderBottom: '2px solid #1976D2' }}>Towers</th>
                              <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: 'bold', borderBottom: '2px solid #1976D2' }}>No. of Copies Printed</th>
                              <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: 'bold', borderBottom: '2px solid #1976D2' }}>Publication</th>
                              <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: 'bold', borderBottom: '2px solid #1976D2' }}>Machine</th>
                              <th style={{ padding: '12px', textAlign: 'center', color: 'white', fontWeight: 'bold', borderBottom: '2px solid #1976D2' }}>Pages</th>
                              <th style={{ padding: '12px', textAlign: 'center', color: 'white', fontWeight: 'bold', borderBottom: '2px solid #1976D2' }}>User</th>
                              <th style={{ padding: '12px', textAlign: 'center', color: 'white', fontWeight: 'bold', borderBottom: '2px solid #1976D2' }}>Location</th>
                            </tr>
                          </thead>
                          <tbody>
                            {towerRecordsData.map((record: any, idx: number) => {
                              const towerList = record.tower_ids && Array.isArray(record.tower_ids) && record.tower_ids.length > 0
                                ? `Tower ${record.tower_ids.join(', Tower ')}`
                                : 'No Tower';
                              
                              return (
                                <tr key={idx} style={{
                                  backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white',
                                  borderBottom: '1px solid #ddd'
                                }}>
                                  <td style={{ padding: '10px', fontSize: '11px', color: '#666' }}>
                                    {record.record_date ? new Date(record.record_date).toLocaleDateString() : '-'}
                                  </td>
                                  <td style={{ padding: '10px', fontSize: '11px', fontWeight: '500', color: '#2196F3' }}>
                                    {towerList}
                                  </td>
                                  <td style={{ padding: '10px', fontSize: '11px', fontWeight: 'bold', color: '#333' }}>
                                    {record.po_number || '-'}
                                  </td>
                                  <td style={{ padding: '10px', fontSize: '11px', color: '#666' }}>
                                    {record.publication_name || record.custom_publication_name || '-'}
                                  </td>
                                  <td style={{ padding: '10px', fontSize: '11px', color: '#666' }}>
                                    {record.machine_name || '-'}
                                  </td>
                                  <td style={{ padding: '10px', textAlign: 'center', fontSize: '11px', color: '#2196F3', fontWeight: '500' }}>
                                    {(record.total_pages || 0).toLocaleString()}
                                  </td>
                                  <td style={{ padding: '10px', textAlign: 'center', fontSize: '11px', color: '#666' }}>
                                    {record.user_name || '-'}
                                  </td>
                                  <td style={{ padding: '10px', textAlign: 'center', fontSize: '11px', color: '#666' }}>
                                    {record.user_location || '-'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p style={{ color: '#999', fontStyle: 'italic' }}>No production records found for the selected filters.</p>
                )}
              </div>
            )}

            {/* INK TAB */}
            {activeTab === 'ink' && (
              <div className="chart-wrapper">
                {(() => {
                  const inkContext = getInkPublicationContext();
                  const inkLabel = inkContext.label || 'VK';
                  return (
                <>
                <h3>{inkLabel} Ink Consumption & Mileage</h3>
                {inkAnalytics?.monthly && inkAnalytics.monthly.length > 0 ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '25px' }}>
                      <div style={{
                        backgroundColor: '#f0f7ff',
                        border: '2px solid #2196F3',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>{inkLabel} ALP</p>
                        <p style={{ margin: 0, color: '#1976D2', fontSize: '24px', fontWeight: 'bold' }}>{formatVarianceNumber(inkAnalytics.totals?.total_alp || 0)}</p>
                      </div>
                      <div style={{
                        backgroundColor: '#e8f5e9',
                        border: '2px solid #4CAF50',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOTAL INK KGS</p>
                        <p style={{ margin: 0, color: '#2E7D32', fontSize: '24px', fontWeight: 'bold' }}>{Number(inkAnalytics.totals?.total_ink_kgs || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
                      </div>
                      <div style={{
                        backgroundColor: '#f0f7ff',
                        border: `2px solid ${INK_COLORS.cyan}`,
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>CYAN YIELD</p>
                        <p style={{ margin: 0, color: INK_COLORS.cyan, fontSize: '24px', fontWeight: 'bold' }}>{formatVarianceNumber(inkAnalytics.totals?.cyan_mileage || 0)}</p>
                      </div>
                      <div style={{
                        backgroundColor: '#fff3e0',
                        border: `2px solid ${INK_COLORS.magenta}`,
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>MAGENTA YIELD</p>
                        <p style={{ margin: 0, color: INK_COLORS.magenta, fontSize: '24px', fontWeight: 'bold' }}>{formatVarianceNumber(inkAnalytics.totals?.magenta_mileage || 0)}</p>
                      </div>
                      <div style={{
                        backgroundColor: '#fffde7',
                        border: `2px solid ${INK_COLORS.yellow}`,
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>YELLOW YIELD</p>
                        <p style={{ margin: 0, color: '#8a6f00', fontSize: '24px', fontWeight: 'bold' }}>{formatVarianceNumber(inkAnalytics.totals?.yellow_mileage || 0)}</p>
                      </div>
                      <div style={{
                        backgroundColor: '#f5f5f5',
                        border: `2px solid ${INK_COLORS.black}`,
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>BLACK YIELD</p>
                        <p style={{ margin: 0, color: '#111', fontSize: '24px', fontWeight: 'bold' }}>{formatVarianceNumber(inkAnalytics.totals?.black_mileage || 0)}</p>
                      </div>
                    </div>
                    <InkConsumptionChart monthlyRows={inkAnalytics.monthly || []} />
                    <InkMileageChart monthlyRows={inkAnalytics.monthly || []} />
                    <div style={{ marginTop: '10px', color: '#666', fontSize: '12px' }}>
                      Formula: {inkLabel} ALP = Σ((copies printed + newsprint waste quantity) × pages). Yield = ALP / ink kg.
                    </div>
                  </>
                ) : (
                  <div className="no-data">No {inkLabel} monthly ink data available for the selected filters.</div>
                )}
                </>
                  );
                })()}
              </div>
            )}

            {/* AI INSIGHTS TAB */}
            {activeTab === 'ai' && (
              <div className="chart-wrapper">
                <h3>AI Maintenance Predictions & Recommendations</h3>
                {aiRecommendations && aiRecommendations.recommendations && aiRecommendations.recommendations.length > 0 ? (
                  <div style={{ width: '100%' }}>
                    {/* Recommendations Summary */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '15px',
                      marginBottom: '25px'
                    }}>
                      <div style={{
                        backgroundColor: '#ffebee',
                        border: '2px solid #f44336',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>URGENT MAINTENANCE</p>
                        <p style={{ margin: 0, color: '#f44336', fontSize: '24px', fontWeight: 'bold' }}>
                          {aiRecommendations.total_urgent || 0}
                        </p>
                      </div>
                      <div style={{
                        backgroundColor: '#fff3e0',
                        border: '2px solid #ff9800',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>NORMAL MAINTENANCE</p>
                        <p style={{ margin: 0, color: '#ff9800', fontSize: '24px', fontWeight: 'bold' }}>
                          {aiRecommendations.total_normal || 0}
                        </p>
                      </div>
                    </div>

                    {/* Recommendations List */}
                    <div style={{ marginBottom: '30px' }}>
                      <h4 style={{ fontSize: '14px', marginBottom: '15px', color: '#333' }}>Maintenance Recommendations</h4>
                      {aiRecommendations.recommendations.map((rec: any, idx: number) => (
                        <div 
                          key={idx}
                          style={{
                            padding: '15px',
                            marginBottom: '12px',
                            border: `3px solid ${rec.priority === 'URGENT' ? '#f44336' : '#ff9800'}`,
                            borderRadius: '8px',
                            backgroundColor: rec.priority === 'URGENT' ? '#ffebee' : '#fff3e0'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                            <h5 style={{ margin: 0, color: '#333', fontSize: '16px' }}>
                              {rec.machine_name}
                            </h5>
                            <span style={{
                              padding: '4px 12px',
                              backgroundColor: rec.priority === 'URGENT' ? '#f44336' : '#ff9800',
                              color: 'white',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {rec.priority}
                            </span>
                          </div>
                          <p style={{ margin: '8px 0', color: '#555', fontSize: '14px' }}>
                            <strong>Recommendation:</strong> {rec.recommendation}
                          </p>
                          <p style={{ margin: '8px 0', color: '#666', fontSize: '13px' }}>
                            <strong>Reason:</strong> {rec.reason}
                          </p>
                          <p style={{ margin: '8px 0', color: '#666', fontSize: '12px' }}>
                            <strong>Suggested Date:</strong> {rec.suggested_date}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="no-data">
                    <p>⏳ Loading AI predictions...</p>
                    <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
                      Make sure the ML service is running on port 5001
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Downtime Detail Modal */}
        {downtimeDetailModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#1976d2', fontSize: '20px' }}>Downtime Breakdown Details</h2>
                <button 
                  onClick={() => setDowntimeDetailModal(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#999',
                  }}
                >
                  ×
                </button>
              </div>

              {downtimeDetails.length > 0 && (
                <div style={{ lineHeight: '1.8' }}>
                  {downtimeDetails.map((detail, idx) => (
                    <div key={idx}>
                      <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #eee' }}>
                        <strong style={{ color: '#333' }}>Reason:</strong>
                        <p style={{ margin: '5px 0 0 0', color: '#666' }}>{detail.reason}</p>
                      </div>

                      <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #eee' }}>
                        <strong style={{ color: '#333' }}>Occurrences:</strong>
                        <p style={{ margin: '5px 0 0 0', color: '#666' }}>{detail.occurrences}</p>
                      </div>

                      <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #eee' }}>
                        <strong style={{ color: '#333' }}>Total Duration:</strong>
                        <p style={{ margin: '5px 0 0 0', color: '#666' }}>{detail.totalDuration}</p>
                      </div>

                      <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #eee' }}>
                        <strong style={{ color: '#333' }}>Average Duration:</strong>
                        <p style={{ margin: '5px 0 0 0', color: '#666' }}>
                          {(() => {
                            const hours = Math.floor(detail.avgDuration / 3600);
                            const mins = Math.floor((detail.avgDuration % 3600) / 60);
                            const secs = detail.avgDuration % 60;
                            return `${hours}h ${mins}m ${secs}s`;
                          })()}
                        </p>
                      </div>

                      <div>
                        <strong style={{ color: '#333' }}>Affected Publications:</strong>
                        <p style={{ margin: '5px 0 0 0', color: '#666', whiteSpace: 'pre-wrap' }}>{detail.publications}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Spreadsheet Viewer Modal */}
        <SpreadsheetViewerModal
          isOpen={spreadsheetViewerOpen}
          data={spreadsheetData}
          onClose={() => {
            setSpreadsheetViewerOpen(false);
            setSpreadsheetData(null);
            setSpreadsheetExportHandler(null);
          }}
          onExport={spreadsheetExportHandler || undefined}
        />
      </div>
    </div>
  );
};

export default AdminDashboard;
